// IsometricBuilder — phase 3 port from oq-images.
//
// Stacked tile layout: each level is a square plate at a fixed Y. Only the
// active level can be edited per turn (matches AGENT.md contract). Reuses
// the GLB asset registry, theme, lighting, and float-shadow effect. Drops
// chat / variants / undo / seed-profile machinery beyond the starter.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three-stdlib";
import { THEMES, applyTheme, setupSceneLights } from "./lib/sceneAssets";
import {
  applyOneQodeShader,
  getInstanceOrPlaceholder,
  loadManifest,
  type CatalogEntry,
  type Manifest,
} from "./lib/assetRegistry";
import { copyToClipboard, saveImage } from "./lib/exportScene";
import { uid, type BrandCanvasPose, type BrandMaterialMode, type BrandTextFont, type RackRowPreset } from "./lib/types";
import { applyBrandMark, clearBrandMark } from "./lib/brandMark";
import { rebuildLinks, tickLinks } from "./lib/links";
import { applyAccentOverride, clearAccentOverride, DEFAULT_ACCENT_HEX } from "./lib/accent";
import {
  addVirtualBrandEntries,
  applyBrandCanvasAppearance,
  applyBrandCanvasTransform,
  createTextLabelObject,
  defaultBrandObjectPatch,
  disposeGeneratedObject,
  isBrandCanvasKind,
  isTextLabelKind,
  resolveBrandCanvasMaterialMode,
  resolveBrandMarkMaterialMode,
  textObjectTag,
} from "./lib/brandObjects";
import {
  addVirtualRackRowEntry,
  createRackRowObject,
  defaultRackRowPatch,
  disposeRackRowObject,
  isRackRowKind,
  rackRowTag,
} from "./lib/rackRow";
import {
  createGuidesGroup,
  defaultGuideSettings,
  dragCoordForRay,
  hitTestGuide,
  rebuildGuides,
} from "./lib/guides";
import type { Guide } from "./lib/types";
import { applyLightingVersion, applyLightingV4Extras, loadLightingVersion, saveLightingVersion, type LightingVersion } from "./lib/lightingVersion";
import { SelectionCard, type SelectionCardHandle } from "./SelectionCard";
import { Tabs } from "./Tabs";
import { registerBlinkInstance, tickBlink } from "./lib/blink";
import { Toast, type ToastState } from "./Toast";
import {
  defaultMainRef,
  deleteLocalProfile,
  deleteServerProfile,
  fetchLocalProfile,
  fetchServerProfile,
  isLocalEditor,
  listLocalProfiles,
  listServerProfiles,
  makeAutoSaver,
  makeLocalRef,
  makeServerRef,
  newLocalSlug,
  readActiveRef,
  renameLocalProfile,
  saveLocalProfile,
  slugFromName,
  writeActiveRef,
  type ProfileRef,
} from "./lib/profileStore";
import type { SceneKind } from "./data/sceneKinds";

// ============================================================
// Types
// ============================================================

type IsoObject = {
  id: string;
  levelId: string;
  kind: SceneKind;
  x: number;
  z: number;
  scale: number;
  rotation: number;
  accent?: boolean;
  /** OneQode brand-mark decal kind, or null/undefined for none. */
  brandMark?: "icon" | "logo" | null;
  /** Face the mark sits on. Ignored when brandMark is null/undefined. */
  brandSide?: "front" | "back" | "left" | "right" | "top" | null;
  /** Thickness multiplier for the brand mark (default 1.0; 0.2..3.0). */
  brandThickness?: number;
  /** Rotate the placed brand mark 180 degrees within the chosen face. */
  brandFlip?: boolean;
  /** Horizontal mirror of the placed brand mark. See lib/types.ts. */
  brandInvert?: boolean;
  /** First-class Brand objects can stand upright or lay flat on the canvas. */
  brandPose?: BrandCanvasPose;
  /** Runtime 3D text content for `text-label` objects. */
  textContent?: string;
  /** Runtime 3D text font choice. */
  textFont?: BrandTextFont;
  /** Runtime 3D text extrusion depth in world units. */
  textDepth?: number;
  /** Material used by standalone brand/text objects and attached brand decals. */
  brandColor?: BrandMaterialMode;
  // ---- rack-row composite controls (only meaningful when kind === "rack-row")
  rowPreset?: RackRowPreset;
  rowCount?: number;
  rowPair?: boolean;
  rowAisleWidth?: number;
};

type IsoLink = {
  id: string;
  fromId: string;
  toId: string;
};

type Level = {
  id: string;
  label: string;
  objects: IsoObject[];
  links?: IsoLink[];
  /** Per-level guides. Each level has its own draggable lines clipped
   *  to that plate's tile bounds and rendered at the plate's Y. */
  guides?: import("./lib/types").Guide[];
};

type IsoSettings = {
  tileSize: number;
  levelSpacing: number;
  cameraYaw: number;
  cameraPitch: number;
  cameraDistance: number;
  fov: number;
  /** Floor plate thickness in metres (defaults to 0.15). */
  plateThickness?: number;
  /** Whether to render the per-plate dot grid overlay. */
  dotGrid?: boolean;
  /** Distance between dot-grid dots on each plate, in metres. */
  dotGridSpacing?: number;
  /** Dot radius as a fraction of cell pitch (0.005..0.10). */
  dotGridSize?: number;
  /** Dot grid material opacity 0..1. */
  dotGridOpacity?: number;
  /** Link tube thickness in metres (default 0.06). */
  linkThickness?: number;
  /** Steady cable opacity 0..1. */
  linkCableOpacity?: number;
  /** Animated power-strip opacity 0..1 (the flowing pulse). */
  linkStripOpacity?: number;
  /** Flow animation speed (0 = stopped). */
  linkSpeed?: number;
  /** Whether power flow is animated. */
  linkPower?: boolean;
  /** Hex string for link colour (e.g. "#0b72b7"). */
  linkColor?: string;
  /** Match Scene view: when true, links go straight A→B instead of the
   *  perpendicular L-bend. */
  straightLinks?: boolean;
  // ---- Guides (draggable on-floor reference lines)
  gridLines?: boolean;
  gridLinesShowCrosses?: boolean;
  gridLinesCrossSize?: number;
  gridLinesOpacityLight?: number;
  gridLinesOpacityDark?: number;
  gridLinesCrossOpacityLight?: number;
  gridLinesCrossOpacityDark?: number;
  gridLinesColorLight?: string;
  gridLinesColorDark?: string;
  gridLinesDashSize?: number;
  gridLinesGapSize?: number;
  guides?: import("./lib/types").Guide[];
};

const CAMERA_PITCH_MIN = 0.02;
const CAMERA_PITCH_MAX = Math.PI / 2 - 0.05;

function formatCameraHeight(pitch: number): string {
  const t = THREE.MathUtils.clamp(
    (pitch - CAMERA_PITCH_MIN) / (CAMERA_PITCH_MAX - CAMERA_PITCH_MIN),
    0,
    1,
  );
  if (t <= 0.03) return "Ground";
  if (t >= 0.97) return "Top";
  return `${Math.round(t * 100)}%`;
}

function middleFloorPivotY(levelCount: number, spacing: number): number {
  return ((Math.max(1, levelCount) - 1) * spacing) / 2;
}

/**
 * Canvas-baked dot grid for an iso plate top. Same visual idiom as the
 * scene-builder floor (round dots on a regular spacing) so the iso view
 * feels like the same world.
 */
function buildIsoPlateDotGrid(
  worldSize: number,
  spacing: number,
  colorHex: number,
  sizeFactor: number = 0.025,
): THREE.CanvasTexture {
  const cellsPerAxis = Math.max(2, Math.round(worldSize / spacing));
  // 8192-px canvas matches Scene-tab's dot grid: doubled linear resolution
  // so small dot factors still produce a sharp disc without bumping into
  // the AA floor.
  const px = 8192;
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const c = canvas.getContext("2d")!;
  c.clearRect(0, 0, px, px);
  const col = new THREE.Color(colorHex);
  c.fillStyle = `rgba(${(col.r * 255) | 0}, ${(col.g * 255) | 0}, ${(col.b * 255) | 0}, 1)`;
  const step = px / cellsPerAxis;
  // Match Scene's lower bound of 0.001 so the slider goes the same range
  // in both views.
  const clampedFactor = THREE.MathUtils.clamp(sizeFactor, 0.001, 0.10);
  const r = Math.max(0.5, step * clampedFactor);
  for (let i = 0; i < cellsPerAxis; i++) {
    for (let j = 0; j < cellsPerAxis; j++) {
      const x = i * step + step / 2;
      const y = j * step + step / 2;
      c.beginPath();
      c.arc(x, y, r, 0, Math.PI * 2);
      c.fill();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

const DEFAULT_SETTINGS: IsoSettings = {
  tileSize: 20,
  levelSpacing: 5.5,
  cameraYaw: Math.PI / 4,
  // Default Height slider position ~16% of the [MIN, MAX] range — a low,
  // grounded iso angle that keeps the plates feeling like floors rather
  // than top-down maps. Matches the formula used by formatCameraHeight().
  cameraPitch: 0.02 + 0.16 * (Math.PI / 2 - 0.05 - 0.02),
  cameraDistance: 42,
  fov: 36,
  plateThickness: 0.15,
  dotGrid: true,
  dotGridSpacing: 1.0,
  dotGridSize: 0.025,
  dotGridOpacity: 0.28,
  ...defaultGuideSettings(),
  // Link defaults match the Scene-main profile's tuned link look (thin,
  // faint, straight) so an empty iso profile starts with the same vibe.
  // Empty linkColor = use the resolver default (white-ish on dark).
  linkThickness: 0.015,
  linkCableOpacity: 0.14,
  linkStripOpacity: 0.2,
  linkSpeed: 0.2,
  linkPower: true,
  linkColor: "",
  straightLinks: true,
};

// Three canonical levels: Compute (top) / Infrastructure (mid) / Network (bottom).
//
// Compute       — GPU clusters optimised for AI training and real-time inference.
// Infrastructure — Power and cooling supporting compute uptime.
// Network       — Private global backbone, routing traffic across the lowest-latency paths.
//
// Object layouts here are tuned for a 20m tile and the master scales now in
// the .blend (rack ≈2 m, AI cluster ≈2.4 m). Coordinates were chosen so every
// object's full footprint fits inside the plate (the off-floor clamp keeps
// dragged objects in too).
function defaultLevels(): Level[] {
  const top = uid();
  const mid = uid();
  const bot = uid();
  return [
    {
      id: top,
      label: "Compute",
      objects: [
        // 4×3 dense GPU farm + a featured AI cluster as the brand accent.
        { id: uid(), levelId: top, kind: "ai-cluster",     x:  0,  z: -5,  scale: 1, rotation: 0, accent: true },
        { id: uid(), levelId: top, kind: "gpu-stack",      x: -6,  z: -2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "gpu-stack",      x: -3,  z: -2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "gpu-stack",      x:  3,  z: -2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "gpu-stack",      x:  6,  z: -2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "dedicated-rack", x: -6,  z:  2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "dedicated-rack", x: -3,  z:  2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "dedicated-rack", x:  0,  z:  2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "dedicated-rack", x:  3,  z:  2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "dedicated-rack", x:  6,  z:  2,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "inference-node", x: -5,  z:  6,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "inference-node", x:  0,  z:  6,  scale: 1, rotation: 0 },
        { id: uid(), levelId: top, kind: "inference-node", x:  5,  z:  6,  scale: 1, rotation: 0 },
      ],
    },
    {
      id: mid,
      label: "Infrastructure",
      objects: [
        // Power + cooling backbone. Modular pods read as substations; control
        // hub centres the floor; storage arrays flank.
        { id: uid(), levelId: mid, kind: "modular-pod",    x: -3,  z: -4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "modular-pod",    x:  3,  z: -4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "control-hub",    x:  0,  z:  0,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "storage-array",  x: -6,  z:  4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "storage-array",  x: -3,  z:  4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "storage-array",  x:  3,  z:  4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "storage-array",  x:  6,  z:  4,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "server-rack",    x: -5,  z: -1,  scale: 1, rotation: 0 },
        { id: uid(), levelId: mid, kind: "server-rack",    x:  5,  z: -1,  scale: 1, rotation: 0 },
      ],
    },
    (() => {
      // Network floor: IXP at centre, 4 POPs ringing it at 8m cardinal radius,
      // 4 subsea cables running edge → IXP, 2 landing stations on the edge,
      // and a small RTR cluster off-centre. IXP-to-POP power lines visualise
      // backbone traffic.
      const ixp = uid();
      const popN = uid();
      const popS = uid();
      const popE = uid();
      const popW = uid();
      const objects: IsoObject[] = [
        { id: ixp,    levelId: bot, kind: "interconnection-hub", x:  0,  z:  0, scale: 1, rotation: 0, accent: true },
        { id: popN,   levelId: bot, kind: "pop-node",            x:  0,  z: -8, scale: 1, rotation: 0 },
        { id: popS,   levelId: bot, kind: "pop-node",            x:  0,  z:  8, scale: 1, rotation: 0 },
        { id: popE,   levelId: bot, kind: "pop-node",            x:  8,  z:  0, scale: 1, rotation: 0 },
        { id: popW,   levelId: bot, kind: "pop-node",            x: -8,  z:  0, scale: 1, rotation: 0 },
        { id: uid(),  levelId: bot, kind: "subsea-cable",        x: -8,  z: -8, scale: 1, rotation: Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "subsea-cable",        x:  8,  z: -8, scale: 1, rotation: -Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "subsea-cable",        x: -8,  z:  8, scale: 1, rotation: -Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "subsea-cable",        x:  8,  z:  8, scale: 1, rotation: Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "landing-station",     x: -9,  z: -9, scale: 1, rotation: Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "landing-station",     x:  9,  z:  9, scale: 1, rotation: Math.PI / 4 },
        { id: uid(),  levelId: bot, kind: "route-router",        x: -3,  z: -3, scale: 1, rotation: 0 },
        { id: uid(),  levelId: bot, kind: "route-router",        x:  3,  z: -3, scale: 1, rotation: 0 },
        { id: uid(),  levelId: bot, kind: "route-router",        x:  0,  z: -3, scale: 1, rotation: 0 },
      ];
      const links: IsoLink[] = [
        { id: uid(), fromId: ixp, toId: popN },
        { id: uid(), fromId: ixp, toId: popS },
        { id: uid(), fromId: ixp, toId: popE },
        { id: uid(), fromId: ixp, toId: popW },
      ];
      return { id: bot, label: "Network", objects, links };
    })(),
  ];
}

// Bumped to v2 when the preset was reorganised to Compute / Infrastructure /
// Network. Localstorage entries under the old key are stale and ignored.
const ISO_KEY = "oneqode-iso-v2";

// Empty bootstrap until the Main (Reiss) profile JSON loads.
function bootstrapEmptyIso(): { settings: IsoSettings; levels: Level[] } {
  return { settings: DEFAULT_SETTINGS, levels: defaultLevels() };
}

// ============================================================
// Component
// ============================================================

export default function IsometricBuilder() {
  const initial = useRef<{ settings: IsoSettings; levels: Level[] } | null>(null);
  if (!initial.current) initial.current = bootstrapEmptyIso();
  const [settings, setSettings] = useState<IsoSettings>(initial.current.settings);
  const [levels, setLevels] = useState<Level[]>(initial.current.levels);
  const [activeLevelId, setActiveLevelId] = useState<string>(initial.current.levels[0].id);
  // Multi-select. Mirrors Scene-view: an array, with `selectedId` derived
  // from the last entry (most recently picked) for any UI/render code that
  // only cares about a single selection. setSelectedId resets to a single
  // id (or clears) — useful in code paths that didn't intend multi.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedId: string | null = selectedIds.length > 0
    ? selectedIds[selectedIds.length - 1]
    : null;
  const setSelectedId = useCallback((id: string | null) => {
    setSelectedIds(id == null ? [] : [id]);
  }, []);
  // See SceneBuilder note: bumped after the world-sync effect mounts new
  // GLB instances so the link-rebuild effect re-fires once their bboxes
  // are real, not empty. Prevents "links don't appear until you tweak a
  // setting" on first load.
  const [instancesVersion, setInstancesVersion] = useState(0);
  const [linkingFromId, setLinkingFromId] = useState<string | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [toast, setToast] = useState<ToastState>(null);
  const editor = isLocalEditor();
  const autoSaverRef = useRef(makeAutoSaver());
  const [activeRef, setActiveRef] = useState<ProfileRef>(() =>
    readActiveRef("iso") ?? defaultMainRef("iso"));
  const [serverList, setServerList] = useState<Array<{ slug: string; kind: "scene" | "iso" }>>([]);
  const [localList, setLocalList] = useState(() => listLocalProfiles("iso"));
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Refs mirroring state for the render loop.
  const settingsRef = useRef(settings); useEffect(() => { settingsRef.current = settings; }, [settings]);
  const levelsRef = useRef(levels);     useEffect(() => { levelsRef.current = levels; }, [levels]);
  const activeLevelIdRef = useRef(activeLevelId); useEffect(() => { activeLevelIdRef.current = activeLevelId; }, [activeLevelId]);
  const selectedIdRef = useRef(selectedId); useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  const selectedIdsRef = useRef(selectedIds); useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);
  const linkingFromIdRef = useRef<string | null>(linkingFromId); useEffect(() => { linkingFromIdRef.current = linkingFromId; }, [linkingFromId]);

  // Undo / redo. Mirrors the Scene-view pattern: snapshots are { levels }
  // only (camera + theme stay out of history so slider drags don't fill
  // the stack). pushSnapshot is called BEFORE each user mutation; undo
  // pops the previous state and pushes the current onto the redo stack.
  type IsoSnap = { levels: Level[] };
  const undoStackRef = useRef<IsoSnap[]>([]);
  const redoStackRef = useRef<IsoSnap[]>([]);
  const HISTORY_LIMIT = 100;
  const pushSnapshot = useCallback(() => {
    undoStackRef.current.push({ levels: levelsRef.current });
    if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
    redoStackRef.current = [];
  }, []);
  const undo = useCallback(() => {
    const prev = undoStackRef.current.pop();
    if (!prev) return;
    redoStackRef.current.push({ levels: levelsRef.current });
    setLevels(prev.levels);
    setSelectedId(null);
    setSelectedGuide(null);
  }, []);
  const redo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push({ levels: levelsRef.current });
    setLevels(next.levels);
    setSelectedId(null);
    setSelectedGuide(null);
  }, []);

  // Live camera state.
  const camYawRef = useRef(settings.cameraYaw);
  const camPitchRef = useRef(settings.cameraPitch);
  const camDistanceRef = useRef(settings.cameraDistance);
  const camTargetRef = useRef(new THREE.Vector3(
    0,
    middleFloorPivotY(initial.current.levels.length, settings.levelSpacing),
    0,
  ));
  useEffect(() => {
    camYawRef.current = settings.cameraYaw;
    camPitchRef.current = settings.cameraPitch;
    camDistanceRef.current = settings.cameraDistance;
  }, [settings.cameraYaw, settings.cameraPitch, settings.cameraDistance]);

  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const platesRef = useRef<THREE.Group | null>(null);          // level plates
  const worldsRef = useRef<Map<string, THREE.Group> | null>(null); // levelId → world group
  const lightsRef = useRef<ReturnType<typeof setupSceneLights> | null>(null);
  const linksGroupsRef = useRef<Map<string, THREE.Group> | null>(null); // levelId → links group
  const selectionCardRef = useRef<SelectionCardHandle | null>(null);
  // Right-panel display tab — local-only state, mirrors Scene view.
  type DisplayTab = "dots" | "guides" | "links";
  const [displayTab, setDisplayTab] = useState<DisplayTab>("dots");
  // One guides Group per level — mounted at that plate's Y so guides
  // render on the plate they belong to and clip to its tile size.
  const guidesGroupsRef = useRef<Map<string, THREE.Group> | null>(null);
  type GuideSel = { levelId: string; id: string };
  const [selectedGuide, setSelectedGuide] = useState<GuideSel | null>(null);
  const selectedGuideRef = useRef<GuideSel | null>(null);
  useEffect(() => { selectedGuideRef.current = selectedGuide; }, [selectedGuide]);
  const guideDragRef = useRef<{
    levelId: string; id: string; axis: "x" | "z"; pointerId: number; planeY: number;
  } | null>(null);
  // Look-only settings shared across every level (colour, opacity, dash, etc.).
  // Per-plate `groundSize` is supplied at render time using `tileSize`.
  const guideLookSettings = useMemo(() => ({
    gridLines: settings.gridLines,
    gridLinesShowCrosses: settings.gridLinesShowCrosses,
    gridLinesCrossSize: settings.gridLinesCrossSize,
    gridLinesOpacityLight: settings.gridLinesOpacityLight,
    gridLinesOpacityDark: settings.gridLinesOpacityDark,
    gridLinesCrossOpacityLight: settings.gridLinesCrossOpacityLight,
    gridLinesCrossOpacityDark: settings.gridLinesCrossOpacityDark,
    gridLinesColorLight: settings.gridLinesColorLight,
    gridLinesColorDark: settings.gridLinesColorDark,
    gridLinesDashSize: settings.gridLinesDashSize,
    gridLinesGapSize: settings.gridLinesGapSize,
    theme: "dark" as const,
    groundSize: settings.tileSize,
  }), [
    settings.gridLines,
    settings.gridLinesShowCrosses,
    settings.gridLinesCrossSize,
    settings.gridLinesOpacityLight,
    settings.gridLinesOpacityDark,
    settings.gridLinesCrossOpacityLight,
    settings.gridLinesCrossOpacityDark,
    settings.gridLinesColorLight,
    settings.gridLinesColorDark,
    settings.gridLinesDashSize,
    settings.gridLinesGapSize,
    settings.tileSize,
  ]);
  // Lighting profile (V1 = original, V2 = matte velvet ref-image vibe).
  const [lightingVersion, setLightingVersionState] = useState<LightingVersion>(() => loadLightingVersion());
  const setLightingVersion = useCallback((v: LightingVersion) => {
    setLightingVersionState(v);
    saveLightingVersion(v);
  }, []);
  const baseEnvIntensityRef = useRef<number>(THEMES.dark.environmentIntensity);
  const selectionOutlineRef = useRef<THREE.Box3Helper | null>(null);
  // Drag state. `id` is the anchor (the object the user clicked on). When
  // the selection includes other ids on the same level, `starts` carries
  // their original (x,z) so the move applies the same delta to every
  // member — group-move, Figma-style. Single-selection is the trivial
  // case where `starts` has just the anchor.
  const draggingRef = useRef<{
    id: string;
    levelId: string;
    offsetX: number;
    offsetZ: number;
    starts: Map<string, { x: number; z: number }>;
  } | null>(null);
  const camDragRef = useRef<{
    mode: "orbit" | "pan";
    startX: number;
    startY?: number;
    startYaw: number;
    /** Snapshot of camTargetRef at drag start. Pan adds the world-space
     *  delta to this; orbit ignores it. */
    startTarget?: THREE.Vector3;
  } | null>(null);

  const setIsoPivotTarget = useCallback(() => {
    camTargetRef.current.set(
      0,
      middleFloorPivotY(levelsRef.current.length, settingsRef.current.levelSpacing),
      0,
    );
  }, []);

  // Load manifest.
  useEffect(() => {
    loadManifest().then(setManifest).catch((e) => setError(String(e)));
  }, []);

  // Discover server + local iso profiles for the picker.
  useEffect(() => {
    void (async () => {
      const all = await listServerProfiles();
      setServerList(all.filter((e) => e.kind === "iso"));
    })();
    setLocalList(listLocalProfiles("iso"));
  }, []);

  // Load active profile when ref changes.
  useEffect(() => {
    let cancelled = false;
    setProfileLoaded(false);
    writeActiveRef(activeRef);
    (async () => {
      type IsoPayload = {
        version: number;
        kind: "iso-profile";
        settings: IsoSettings;
        levels: Level[];
      };
      let payload: IsoPayload | null = null;
      if (activeRef.source === "server") {
        payload = await fetchServerProfile<IsoPayload>(activeRef.slug);
      } else {
        payload = fetchLocalProfile<IsoPayload>("iso", activeRef.slug);
      }
      if (cancelled) return;
      // Migration: profiles saved before the Compute / Infrastructure /
      // Network rename used labels like "Top — compute". Detect any of those
      // and replace with the fresh defaultLevels() preset so the new view is
      // canonical from first load.
      const STALE_LABELS = new Set([
        "Top — compute",
        "Mid — networking & storage",
        "Bottom — power & utilities",
      ]);
      const isStale = payload?.levels?.some((l: Level) => STALE_LABELS.has(l.label));
      if (payload && !isStale) {
        if (payload.settings) setSettings({ ...DEFAULT_SETTINGS, ...payload.settings });
        if (Array.isArray(payload.levels) && payload.levels.length) {
          setLevels(payload.levels);
          setActiveLevelId(payload.levels[0].id);
        }
      } else if (isStale) {
        // Fall through with defaultLevels (already in initial state). Force
        // settings to the new defaults (tileSize 20).
        setSettings(DEFAULT_SETTINGS);
        const fresh = defaultLevels();
        setLevels(fresh);
        setActiveLevelId(fresh[0].id);
      }
      setSelectedId(null);
      setProfileLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [activeRef]);

  useEffect(() => {
    return autoSaverRef.current.subscribe((s) => setSaveStatus(s));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 1800);
    return () => window.clearTimeout(timer);
  }, [toast?.id]);

  useEffect(() => {
    if (!profileLoaded) return;
    if (activeRef.source === "server" && !editor) return;
    const payload = {
      version: 1,
      kind: "iso-profile" as const,
      name: activeRef.name,
      settings,
      levels,
      updatedAt: Date.now(),
    };
    autoSaverRef.current.trigger(activeRef, payload);
    if (activeRef.source === "local") {
      setLocalList(listLocalProfiles("iso"));
    }
  }, [settings, levels, profileLoaded, editor, activeRef]);

  // Sync settings on user edits to camera state via refs.
  const commitCamera = useCallback(() => {
    // NOTE: do not reset the orbit target here. Pan drags update
    // camTargetRef and we want them to stick. Orbit re-anchors the
    // target itself at drag start (see camModeFor handler).
    setSettings((p) => ({
      ...p,
      cameraYaw: camYawRef.current,
      cameraPitch: camPitchRef.current,
      cameraDistance: camDistanceRef.current,
    }));
  }, []);

  // ---------- boot scene ----------
  useEffect(() => {
    if (!manifest || !mountRef.current) return;
    const mount = mountRef.current;

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Match the Scene tab's renderer + tonemap so the iso view shares the
    // same lighting character (do NOT regress: matches sceneAssets / SceneBuilder).
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.10;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(THEMES.dark.bg);
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color(THEMES.dark.bg);

    // Soft IBL.
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = THEMES.dark.environmentIntensity;
    baseEnvIntensityRef.current = THEMES.dark.environmentIntensity;
    pmrem.dispose();
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);

    const camera = new THREE.PerspectiveCamera(settings.fov, 1, 0.1, 600);
    cameraRef.current = camera;

    // Lights — reuse the canonical key/fill/hemi from sceneAssets but tuned
    // for the iso aesthetic (cap edges read clean against the dark plates).
    const lights = setupSceneLights(scene);
    lightsRef.current = lights;
    // Force theme palette onto a dummy ground/dot pair so applyTheme works.
    // We don't actually use a ground here; plates are level platforms.
    const dummyGround = new THREE.Mesh(
      new THREE.PlaneGeometry(0.01, 0.01),
      new THREE.MeshStandardMaterial(),
    );
    dummyGround.visible = false;
    scene.add(dummyGround);
    const dummyDotMat = new THREE.MeshBasicMaterial();
    applyTheme("dark", scene, renderer, dummyGround, dummyDotMat, lights.hemi, lights.key, lights.fill, lights.rim, lights.floorGlow);
    // The Scene tab's floor-glow point light is positioned at the world-origin
    // floor; in the iso view the plate stack centres around y=middleFloorPivotY,
    // so move the glow up to live just above the middle plate.
    if (lights.floorGlow) {
      const stackY = middleFloorPivotY(levelsRef.current.length, settingsRef.current.levelSpacing);
      lights.floorGlow.position.set(0, stackY + 1.4, 0);
    }

    // Plates group + per-level world groups.
    const plates = new THREE.Group();
    plates.name = "iso-plates";
    scene.add(plates);
    platesRef.current = plates;

    // Per-level guide overlays — populated by the rebuild effect below.
    // Stored in scene-space (not parented to the world groups) so each
    // group can be lifted to its plate's Y independently.
    const guidesGroups = new Map<string, THREE.Group>();
    guidesGroupsRef.current = guidesGroups;

    const worlds = new Map<string, THREE.Group>();
    worldsRef.current = worlds;
    rebuildPlatesAndWorlds(plates, worlds, scene, levels, settings);

    // Eagerly build the guide overlays during scene init so they render
    // on first paint. The deps-driven rebuild effect below would only
    // re-run when its dependencies change, but those are stable from
    // initial bootstrap → first mount, so guides would otherwise stay
    // invisible until the user wiggled a setting. Mirrors the dot-grid
    // path which gets seeded via rebuildPlatesAndWorlds() above.
    {
      const spacing = settings.levelSpacing;
      const stackTopY = (levels.length - 1) * spacing;
      const initialLook = {
        gridLines: settings.gridLines,
        gridLinesShowCrosses: settings.gridLinesShowCrosses,
        gridLinesCrossSize: settings.gridLinesCrossSize,
        gridLinesOpacityLight: settings.gridLinesOpacityLight,
        gridLinesOpacityDark: settings.gridLinesOpacityDark,
        gridLinesCrossOpacityLight: settings.gridLinesCrossOpacityLight,
        gridLinesCrossOpacityDark: settings.gridLinesCrossOpacityDark,
        gridLinesColorLight: settings.gridLinesColorLight,
        gridLinesColorDark: settings.gridLinesColorDark,
        gridLinesDashSize: settings.gridLinesDashSize,
        gridLinesGapSize: settings.gridLinesGapSize,
      };
      levels.forEach((lvl, idx) => {
        const g = createGuidesGroup();
        scene.add(g);
        guidesGroups.set(lvl.id, g);
        g.position.y = stackTopY - idx * spacing + 0.001;
        rebuildGuides(g, { ...initialLook, guides: lvl.guides ?? [] }, null);
      });
    }

    // Per-level link groups: parented to the scene (NOT the world group), so
    // the world coords returned by setFromObject in lib/links.ts arrive
    // unmolested. The mesh sits in scene-space; positions naturally encode
    // each level's stack height because they were captured from world bboxes.
    const linksGroups = new Map<string, THREE.Group>();
    linksGroupsRef.current = linksGroups;

    // ---------- pointer handlers ----------
    const raycaster = new THREE.Raycaster();
    const mouseNDC = new THREE.Vector2();

    function ndcAt(clientX: number, clientY: number) {
      const r = renderer.domElement.getBoundingClientRect();
      mouseNDC.set(
        ((clientX - r.left) / r.width) * 2 - 1,
        -((clientY - r.top) / r.height) * 2 + 1,
      );
    }

    function planeYFor(levelIdx: number, total: number): number {
      const spacing = settingsRef.current.levelSpacing;
      const stackTopY = (total - 1) * spacing;
      return stackTopY - levelIdx * spacing;
    }

    function pickInstance(clientX: number, clientY: number): { id: string; levelId: string; object: THREE.Object3D } | null {
      ndcAt(clientX, clientY);
      raycaster.setFromCamera(mouseNDC, camera);
      const candidates = Array.from(worlds.values()).flatMap((wg) => wg.children);
      const hits = raycaster.intersectObjects(candidates, true);
      for (const h of hits) {
        let cur: THREE.Object3D | null = h.object;
        while (cur && !cur.userData?.id && cur.parent) cur = cur.parent;
        const id = cur?.userData?.id as string | undefined;
        const levelId = cur?.userData?.levelId as string | undefined;
        if (cur && id && levelId) return { id, levelId, object: cur };
      }
      return null;
    }

    function pickFloor(clientX: number, clientY: number): string | null {
      const platesGroup = platesRef.current;
      if (!platesGroup) return null;
      ndcAt(clientX, clientY);
      raycaster.setFromCamera(mouseNDC, camera);
      const hits = raycaster.intersectObjects(platesGroup.children, true);
      for (const h of hits) {
        let cur: THREE.Object3D | null = h.object;
        while (cur && !cur.userData?.levelId && cur.parent) cur = cur.parent;
        const levelId = cur?.userData?.levelId as string | undefined;
        if (levelId) return levelId;
      }
      return null;
    }

    function dragPlaneForLevel(levelId: string) {
      const idx = levelsRef.current.findIndex((l) => l.id === levelId);
      if (idx === -1) return null;
      const y = planeYFor(idx, levelsRef.current.length);
      return { plane: new THREE.Plane(new THREE.Vector3(0, 1, 0), -y), y };
    }

    function planeHit(clientX: number, clientY: number, y: number) {
      ndcAt(clientX, clientY);
      raycaster.setFromCamera(mouseNDC, camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);
      const out = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, out)) return out;
      return null;
    }

    function camModeFor(e: PointerEvent): "orbit" | "pan" | null {
      if (e.button === 1) return "pan";                                          // middle-mouse → floor pan
      if (e.button === 2) return "orbit";
      if (e.button === 0 && e.altKey && !e.shiftKey) return "orbit";
      if (e.button === 0 && (e.metaKey || e.ctrlKey) && !e.altKey) return "pan"; // cmd/ctrl-left fallback for trackpads
      return null;
    }

    /** Natural-direction floor pan: dragging the cursor scrolls the
     *  floor with it (cursor right → floor moves right; cursor down →
     *  floor scrolls down). The orbit target moves OPPOSITE the drag.
     *  Always XZ — Y is locked to the start so the pivot never lifts
     *  off the plate stack. Mirrors the Scene-view pan exactly. */
    function panTarget(dx: number, dy: number, distance: number) {
      const drag = camDragRef.current;
      if (!drag || !drag.startTarget) return;
      const pixelToWorld = distance * 0.0015;
      const right = new THREE.Vector3();
      const fwd = new THREE.Vector3();
      camera.matrixWorld.extractBasis(right, new THREE.Vector3(), fwd);
      fwd.negate();
      right.y = 0; right.normalize();
      fwd.y   = 0; fwd.normalize();
      const target = drag.startTarget.clone();
      // Direct-manipulation pan, both axes (matches Scene-view):
      //   cursor-RIGHT → camera LEFT → floor slides right with the cursor.
      //   cursor-DOWN  → camera FORWARD → floor slides down with the cursor.
      target.addScaledVector(right, -dx * pixelToWorld);
      target.addScaledVector(fwd,    dy * pixelToWorld);
      target.y = drag.startTarget.y;
      camTargetRef.current.copy(target);
    }

    function onPointerDown(e: PointerEvent) {
      const isAltLeft = e.button === 0 && e.altKey;
      const isPanLeft = e.button === 0 && (e.metaKey || e.ctrlKey) && !e.altKey;
      const mode = camModeFor(e);

      if (mode && !isAltLeft && !isPanLeft) {
        e.preventDefault();
        (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);
        // Orbit re-centres on the plate stack each time so the rotation
        // pivot stays at the middle floor; pan keeps whatever target was
        // set previously so the user's pan persists across orbits.
        if (mode === "orbit") setIsoPivotTarget();
        camDragRef.current = {
          mode,
          startX: e.clientX,
          startY: e.clientY,
          startYaw: camYawRef.current,
          startTarget: camTargetRef.current.clone(),
        };
        return;
      }
      // Cmd/Ctrl-left for pan (trackpad fallback).
      if (isPanLeft && mode === "pan") {
        e.preventDefault();
        (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);
        camDragRef.current = {
          mode: "pan",
          startX: e.clientX,
          startY: e.clientY,
          startYaw: camYawRef.current,
          startTarget: camTargetRef.current.clone(),
        };
        return;
      }
      if (e.button !== 0) return;
      (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);

      // ----- Guides: click-select + drag (before object pick). Alt-drag
      // duplicates the guide. Each level has its own guides group at the
      // plate's Y; we walk all groups and pick the closest hit.
      const groups = guidesGroupsRef.current;
      if (groups && settingsRef.current.gridLines) {
        ndcAt(e.clientX, e.clientY);
        raycaster.setFromCamera(mouseNDC, camera);
        let pick: { levelId: string; id: string; axis: "x" | "z"; planeY: number } | null = null;
        for (const [levelId, group] of groups) {
          const dp = dragPlaneForLevel(levelId);
          if (!dp) continue;
          // Translate the group's world Y into the hit-test ray space by
          // running it on the group transform (group is already lifted).
          const local = hitTestGuide(group, raycaster);
          if (local) {
            pick = { levelId, id: local.id, axis: local.axis, planeY: dp.y };
            break;
          }
        }
        if (pick) {
          e.preventDefault();
          let dragId = pick.id;
          const lvl = levelsRef.current.find((l) => l.id === pick!.levelId);
          if (e.altKey && lvl) {
            const src = (lvl.guides ?? []).find((g) => g.id === pick!.id);
            if (src) {
              const clone: Guide = {
                id: Math.random().toString(36).slice(2, 10),
                axis: src.axis,
                coord: src.coord,
              };
              pushSnapshot();
              setLevels((prev) => prev.map((l) =>
                l.id !== pick!.levelId ? l : { ...l, guides: [...(l.guides ?? []), clone] },
              ));
              dragId = clone.id;
            }
          }
          activeLevelIdRef.current = pick.levelId;
          setActiveLevelId(pick.levelId);
          setSelectedGuide({ levelId: pick.levelId, id: dragId });
          guideDragRef.current = {
            levelId: pick.levelId, id: dragId, axis: pick.axis, pointerId: e.pointerId, planeY: pick.planeY,
          };
          return;
        }
      }

      const hit = pickInstance(e.clientX, e.clientY);

      // Linking mode: a previous click on "+ Connect to…" set linkingFromId.
      // The next click on a peer object completes the link and exits linking
      // mode. A click on empty space cancels.
      const linkingFrom = linkingFromIdRef.current;
      if (linkingFrom) {
        e.preventDefault();
        if (hit && hit.id !== linkingFrom) {
          // Find the source's level so the link is filed under it.
          const fromLvl = levelsRef.current.find((l) =>
            l.objects.some((o) => o.id === linkingFrom),
          );
          if (fromLvl && fromLvl.id === hit.levelId) {
            pushSnapshot();
            setLevels((prev) =>
              prev.map((l) => {
                if (l.id !== fromLvl.id) return l;
                const links = l.links ?? [];
                if (links.some((k) =>
                  (k.fromId === linkingFrom && k.toId === hit.id) ||
                  (k.fromId === hit.id && k.toId === linkingFrom)
                )) return l;
                return { ...l, links: [...links, { id: uid(), fromId: linkingFrom, toId: hit.id }] };
              }),
            );
          }
        }
        // Either way, clear linking mode.
        linkingFromIdRef.current = null;
        setLinkingFromId(null);
        return;
      }

      if (!hit && isAltLeft && mode) {
        e.preventDefault();
        setIsoPivotTarget();
        camDragRef.current = {
          mode,
          startX: e.clientX,
          startYaw: camYawRef.current,
        };
        return;
      }

      if (hit) {
        const { id, levelId } = hit;
        activeLevelIdRef.current = levelId;
        setActiveLevelId(levelId);

        // Shift-click toggles the picked id in/out of the selection set
        // and does NOT start a drag — matches Scene-view behaviour. Guides
        // and objects don't co-select (guide selection lives in its own
        // state and would have been handled earlier in this pointerdown).
        if (e.shiftKey) {
          const cur = selectedIdsRef.current;
          if (cur.includes(id)) setSelectedIds(cur.filter((x) => x !== id));
          else setSelectedIds([...cur, id]);
          return;
        }

        const dp = dragPlaneForLevel(levelId);
        const obj = levelObjects(levelId).find((o) => o.id === id);
        if (obj && dp) {
          const p = planeHit(e.clientX, e.clientY, dp.y);
          if (p && e.altKey) {
            e.preventDefault();
            const duplicate: IsoObject = { ...obj, id: uid(), levelId };
            pushSnapshot();
            setLevels((prev) =>
              prev.map((l) =>
                l.id !== levelId
                  ? l
                  : { ...l, objects: [...l.objects, duplicate] },
              ),
            );
            setSelectedId(duplicate.id);
            draggingRef.current = {
              id: duplicate.id,
              levelId,
              offsetX: duplicate.x - p.x,
              offsetZ: duplicate.z - p.z,
              // Alt-drag clone is single-anchor — group cloning isn't a
              // common Figma pattern and would explode with shift-selected
              // groups. Keep the duplicate solo.
              starts: new Map([[duplicate.id, { x: duplicate.x, z: duplicate.z }]]),
            };
            return;
          }
          if (p) {
            // Snap-then-mutate: snapshot before the move so a single
            // Cmd+Z reverts the drag.
            pushSnapshot();
            // Capture every selected member's start position so the
            // pointermove handler can apply a uniform delta. Members on
            // OTHER levels are excluded (iso drag is plate-locked).
            const cur = selectedIdsRef.current;
            const groupIds = cur.includes(id) ? cur : [id];
            const lvlObjs = levelObjects(levelId);
            const starts = new Map<string, { x: number; z: number }>();
            for (const sid of groupIds) {
              const s = lvlObjs.find((o) => o.id === sid);
              if (s) starts.set(sid, { x: s.x, z: s.z });
            }
            draggingRef.current = {
              id,
              levelId,
              offsetX: obj.x - p.x,
              offsetZ: obj.z - p.z,
              starts,
            };
          }
        }
        // Plain click on an already-selected object keeps the set; click
        // on a new object replaces with single. Drag (above) only knows
        // about the anchor id; multi-select group-move is wired in
        // onPointerMove via selectedIdsRef + offsets relative to anchor.
        const cur = selectedIdsRef.current;
        if (!cur.includes(id)) setSelectedIds([id]);
      } else {
        const floorId = pickFloor(e.clientX, e.clientY);
        if (floorId) {
          activeLevelIdRef.current = floorId;
          setActiveLevelId(floorId);
        }
        setSelectedId(null);
        setSelectedGuide(null);
      }
    }

    function onPointerMove(e: PointerEvent) {
      // Guide drag — slide along its only valid axis on its plate. Snap
      // to 0.5 m unless Shift is held (free drag). The plate Y comes from
      // the dragged guide's level, so the ray is projected against the
      // right surface even when other levels float above/below.
      const gDrag = guideDragRef.current;
      if (gDrag) {
        ndcAt(e.clientX, e.clientY);
        raycaster.setFromCamera(mouseNDC, camera);
        const snap = e.shiftKey ? 0 : 0.5;
        const next = dragCoordForRay(gDrag.axis, raycaster, snap, gDrag.planeY);
        if (next === null) return;
        const half = settingsRef.current.tileSize / 2 - 0.25;
        const clamped = THREE.MathUtils.clamp(next, -half, half);
        setLevels((prev) => prev.map((l) =>
          l.id !== gDrag.levelId ? l : {
            ...l,
            guides: (l.guides ?? []).map((g) =>
              g.id === gDrag.id ? { ...g, coord: clamped } : g,
            ),
          },
        ));
        return;
      }

      const drag = draggingRef.current;
      if (drag) {
        const dp = dragPlaneForLevel(drag.levelId);
        if (!dp) return;
        const p = planeHit(e.clientX, e.clientY, dp.y);
        if (!p) return;
        let nx = p.x + drag.offsetX;
        let nz = p.z + drag.offsetZ;
        // Off-floor guard: clamp so the dragged object's full footprint stays
        // inside the plate. Looks up the dragged instance's world bbox to get
        // its half-extents in X/Z, then shrinks the allowed plate region by
        // those amounts. Falls back to a small default if the instance isn't
        // mounted yet.
        const tile = settingsRef.current.tileSize;
        const worlds = worldsRef.current;
        const wg = worlds?.get(drag.levelId);
        const inst = wg?.children.find((c) => c.userData?.id === drag.id);
        let halfX = tile / 2 - 0.5;
        let halfZ = tile / 2 - 0.5;
        if (inst) {
          const bbox = new THREE.Box3().setFromObject(inst);
          const fpX = (bbox.max.x - bbox.min.x) / 2;
          const fpZ = (bbox.max.z - bbox.min.z) / 2;
          halfX = Math.max(0.05, tile / 2 - fpX);
          halfZ = Math.max(0.05, tile / 2 - fpZ);
        }
        nx = THREE.MathUtils.clamp(nx, -halfX, halfX);
        nz = THREE.MathUtils.clamp(nz, -halfZ, halfZ);
        // Group move: compute the delta from the anchor's start, then
        // apply it to every selected member. Each member is then clamped
        // independently to the plate so the group never leaves the floor.
        const anchorStart = drag.starts.get(drag.id);
        if (!anchorStart) return;
        const dX = nx - anchorStart.x;
        const dZ = nz - anchorStart.z;
        setLevels((prev) =>
          prev.map((l) => {
            if (l.id !== drag.levelId) return l;
            return {
              ...l,
              objects: l.objects.map((o) => {
                const start = drag.starts.get(o.id);
                if (!start) return o;
                return {
                  ...o,
                  x: THREE.MathUtils.clamp(start.x + dX, -halfX, halfX),
                  z: THREE.MathUtils.clamp(start.z + dZ, -halfZ, halfZ),
                };
              }),
            };
          }),
        );
        return;
      }
      const cd = camDragRef.current;
      if (cd) {
        const dx = e.clientX - cd.startX;
        const dy = e.clientY - (cd.startY ?? cd.startX);
        if (cd.mode === "pan") {
          panTarget(dx, dy, camDistanceRef.current);
        } else {
          const deltaYaw = -dx * 0.0075;
          camYawRef.current = cd.startYaw + deltaYaw;
          setIsoPivotTarget();
        }
      }
    }

    function onPointerUp(e: PointerEvent) {
      (renderer.domElement as HTMLCanvasElement).releasePointerCapture?.(e.pointerId);
      if (guideDragRef.current && guideDragRef.current.pointerId === e.pointerId) {
        guideDragRef.current = null;
      }
      if (camDragRef.current) commitCamera();
      draggingRef.current = null;
      camDragRef.current = null;
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const factor = Math.exp(e.deltaY * 0.001);
      camDistanceRef.current = THREE.MathUtils.clamp(camDistanceRef.current * factor, 6, 200);
      setSettings((p) => ({ ...p, cameraDistance: camDistanceRef.current }));
    }

    function onContextMenu(e: MouseEvent) { e.preventDefault(); }

    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      // Cmd/Ctrl-Z = undo, Cmd/Ctrl-Shift-Z = redo. Mirrors Scene-view.
      const isUndoCombo = (e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z");
      if (isUndoCombo) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      // Escape cancels link-in-progress (mirrors Scene-view).
      if (e.key === "Escape" && linkingFromIdRef.current) {
        linkingFromIdRef.current = null;
        setLinkingFromId(null);
        e.preventDefault();
        return;
      }
      // Guide-delete short-circuits before the object-selection guard so a
      // selected guide can be removed even when no scene object is selected.
      if (selectedGuideRef.current && (e.key === "Backspace" || e.key === "Delete")) {
        const sel = selectedGuideRef.current;
        pushSnapshot();
        setLevels((prev) => prev.map((l) =>
          l.id !== sel.levelId ? l : {
            ...l,
            guides: (l.guides ?? []).filter((g) => g.id !== sel.id),
          },
        ));
        setSelectedGuide(null);
        e.preventDefault();
        return;
      }
      const ids = selectedIdsRef.current;
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        pushSnapshot();
        // Delete all selected, scoped to the active level (consistent
        // with the rest of iso — drags are plate-locked too).
        setLevels((prev) =>
          prev.map((l) => l.id !== activeLevelIdRef.current ? l : {
            ...l,
            objects: l.objects.filter((o) => !idSet.has(o.id)),
            // Also drop any links whose endpoints were just deleted.
            links: (l.links ?? []).filter((k) => !idSet.has(k.fromId) && !idSet.has(k.toId)),
          }),
        );
        setSelectedIds([]);
      } else if (e.key === "r" || e.key === "R") {
        pushSnapshot();
        setLevels((prev) =>
          prev.map((l) => l.id !== activeLevelIdRef.current ? l : {
            ...l,
            objects: l.objects.map((o) =>
              idSet.has(o.id) ? { ...o, rotation: o.rotation + Math.PI / 2 } : o,
            ),
          }),
        );
      }
    }

    function levelObjects(levelId: string): IsoObject[] {
      return (levelsRef.current.find((l) => l.id === levelId)?.objects ?? []);
    }

    const dom = renderer.domElement;
    dom.addEventListener("pointerdown", onPointerDown);
    dom.addEventListener("pointermove", onPointerMove);
    dom.addEventListener("pointerup", onPointerUp);
    dom.addEventListener("pointercancel", onPointerUp);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKey);

    function onResize() {
      const w = mount.clientWidth, h = mount.clientHeight;
      // Keep Three's drawing buffer and CSS box in sync. Passing
      // updateStyle=false here previously made the camera target appear pinned
      // toward the bottom-right even when the pivot math was correct.
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    onResize();
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    let raf = 0;
    function tick() {
      const s = settingsRef.current;
      // Isometric orbit is anchored at the center of the middle floor so the
      // level stack stays centered on screen. Selection, active level changes,
      // and prior camera drags must never move this pivot.
      setIsoPivotTarget();
      const t = camTargetRef.current;
      const yaw = camYawRef.current;
      // Per-frame defensive clamp keeps lookAt() from going degenerate at the
      // poles while still allowing the Height slider to reach near-ground and
      // near-top-down views.
      const pitch = THREE.MathUtils.clamp(camPitchRef.current, CAMERA_PITCH_MIN, CAMERA_PITCH_MAX);
      camPitchRef.current = pitch;
      const d = camDistanceRef.current;
      const cosP = Math.cos(pitch);
      const sinP = Math.sin(pitch);
      camera.position.set(
        t.x + d * cosP * Math.cos(yaw),
        t.y + d * sinP,
        t.z + d * cosP * Math.sin(yaw),
      );
      camera.up.set(0, 1, 0);
      camera.lookAt(t);
      if (camera.fov !== s.fov) {
        camera.fov = s.fov;
        camera.updateProjectionMatrix();
      }
      // Tick all level link shaders so the flow animation progresses.
      const now = performance.now() * 0.001;
      const lg = linksGroupsRef.current;
      if (lg) {
        for (const g of lg.values()) tickLinks(g, now, s.linkSpeed ?? 0.2);
      }
      // Tick subtle per-object emissive pulse + occasional flicker.
      tickBlink(now, 0.35);

      // Per-frame selection outline. Updates the Box3Helper in place when the
      // selected object moves or is freshly mounted; tears it down when
      // selection clears or the target disappears.
      {
        const sel = selectedIdRef.current;
        const wmap = worldsRef.current;
        let target: THREE.Object3D | null = null;
        if (sel && wmap) {
          for (const wg of wmap.values()) {
            const found = wg.children.find((c) => c.userData?.id === sel);
            if (found) { target = found; break; }
          }
        }
        if (!target) {
          if (selectionOutlineRef.current) {
            scene.remove(selectionOutlineRef.current);
            selectionOutlineRef.current = null;
          }
        } else {
          const box = new THREE.Box3().setFromObject(target);
          let helper = selectionOutlineRef.current;
          if (!helper) {
            helper = new THREE.Box3Helper(box, new THREE.Color(0x4a8eff));
            selectionOutlineRef.current = helper;
            scene.add(helper);
          } else {
            (helper.box as THREE.Box3).copy(box);
            helper.updateMatrixWorld(true);
          }
        }
      }
      // Project selected object's anchor to screen-space for the floating
      // SelectionCard. We sample the bbox top-centre so the card sits just
      // above the object regardless of camera angle.
      const selId = selectedIdRef.current;
      const card = selectionCardRef.current;
      const worlds = worldsRef.current;
      if (card) {
        // Hide while dragging (object or camera) so the card isn't in the way.
        const isDragging = draggingRef.current !== null || camDragRef.current !== null;
        if (!selId || !worlds || isDragging) {
          card.setAnchor(null);
        } else {
          let target: THREE.Object3D | null = null;
          for (const wg of worlds.values()) {
            const found = wg.children.find((c) => c.userData?.id === selId);
            if (found) { target = found; break; }
          }
          if (!target) {
            card.setAnchor(null);
          } else {
            const box = new THREE.Box3().setFromObject(target);
            const anchor = new THREE.Vector3(
              (box.min.x + box.max.x) / 2,
              box.max.y,
              (box.min.z + box.max.z) / 2,
            );
            anchor.project(camera);
            const rect = renderer.domElement.getBoundingClientRect();
            const mountRect = mount.getBoundingClientRect();
            const px = ((anchor.x + 1) / 2) * rect.width + (rect.left - mountRect.left);
            const py = ((-anchor.y + 1) / 2) * rect.height + (rect.top - mountRect.top);
            // Hide if behind camera (z > 1) — projection wraps around.
            if (anchor.z > 1) card.setAnchor(null);
            else card.setAnchor({ x: px, y: py });
          }
        }
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      dom.removeEventListener("pointerdown", onPointerDown);
      dom.removeEventListener("pointermove", onPointerMove);
      dom.removeEventListener("pointerup", onPointerUp);
      dom.removeEventListener("pointercancel", onPointerUp);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKey);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  // Re-apply the lighting version when the user toggles V1/V2/V3/V4.
  useEffect(() => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);
    const lights = lightsRef.current;
    if (lights) {
      // Iso doesn't have a top-level ground/dotMat the way Scene does — the
      // floor is plates and per-plate dot grids. v4-extras still does the
      // useful work on bg/fog/lights; pass throwaway dummies for ground/dotMat
      // so the helper's signature is satisfied.
      const dummyGround = new THREE.Mesh(
        new THREE.PlaneGeometry(0.01, 0.01),
        new THREE.MeshStandardMaterial(),
      );
      const dummyDotMat = new THREE.MeshBasicMaterial();
      applyLightingV4Extras(scene, renderer, lights, dummyDotMat, dummyGround, lightingVersion, THEMES.dark);
      dummyGround.geometry.dispose();
      (dummyGround.material as THREE.Material).dispose();
      dummyDotMat.dispose();
    }
  }, [lightingVersion]);

  // Rebuild plates + worlds when levels metadata changes (count / labels /
  // tile size / spacing). Object placements are handled in a separate effect
  // so we don't rebuild platforms on every drag tick.
  useEffect(() => {
    const scene = sceneRef.current;
    const plates = platesRef.current;
    const worlds = worldsRef.current;
    if (!scene || !plates || !worlds) return;
    rebuildPlatesAndWorlds(plates, worlds, scene, levels, settings);
    setIsoPivotTarget();
  }, [
    levels.length,
    settings.tileSize,
    settings.levelSpacing,
    settings.plateThickness,
    settings.dotGridSpacing,
    settings.dotGridSize,
    settings.dotGridOpacity,
    setIsoPivotTarget,
  ]);

  // Lightweight dot-grid visibility toggle — flips visibility on every
  // plate's dot plane without rebuilding the heavy plate+world geometry.
  // Default-on when settings.dotGrid is undefined (legacy profiles).
  useEffect(() => {
    const plates = platesRef.current;
    if (!plates) return;
    const visible = settings.dotGrid !== false;
    plates.traverse((c) => {
      if ((c as any).userData?.isDotGrid) c.visible = visible;
    });
  }, [settings.dotGrid]);

  // Per-level guide overlays. One Group per level, mounted at that
  // plate's Y. Adds groups for new levels, removes groups for deleted
  // levels, and rebuilds each level's geometry from `level.guides`.
  useEffect(() => {
    const scene = sceneRef.current;
    const groups = guidesGroupsRef.current;
    if (!scene || !groups) return;
    const wantedIds = new Set(levels.map((l) => l.id));
    // Drop overlays for levels that no longer exist.
    for (const [id, g] of groups) {
      if (!wantedIds.has(id)) {
        scene.remove(g);
        // dispose contents
        for (let i = g.children.length - 1; i >= 0; i--) {
          const c = g.children[i];
          (c as any).geometry?.dispose?.();
          const mat = (c as any).material;
          mat?.dispose?.();
        }
        groups.delete(id);
      }
    }
    // Build / refresh per level.
    levels.forEach((lvl, idx) => {
      let g = groups.get(lvl.id);
      if (!g) {
        g = createGuidesGroup();
        scene.add(g);
        groups.set(lvl.id, g);
      }
      // Lift to this plate's Y. This must match `planeYFor()` inside the
      // init effect — top level at highest Y, descending by `spacing`
      // per index. Otherwise guides float between or below the plates
      // instead of sitting on them.
      const spacing = settings.levelSpacing;
      const stackTopY = (levels.length - 1) * spacing;
      const plateY = stackTopY - idx * spacing;
      g.position.y = plateY + 0.001;
      // Render only this level's guides; selection on a different level
      // doesn't affect this rebuild.
      const sel = selectedGuide && selectedGuide.levelId === lvl.id ? selectedGuide.id : null;
      rebuildGuides(
        g,
        { ...guideLookSettings, guides: lvl.guides ?? [] },
        sel,
      );
    });
  }, [levels, guideLookSettings, selectedGuide, settings.levelSpacing]);

  // Sync object placements per level with the world groups.
  useEffect(() => {
    const worlds = worldsRef.current;
    if (!worlds) return;
    let cancelled = false;
    (async () => {
      // Track if we mount any new instance this pass so we can kick the
      // link rebuild after async GLB loads complete (otherwise the
      // initial link-rebuild reads empty bboxes and renders nothing).
      let addedAny = false;
      for (let li = 0; li < levels.length; li++) {
        const lvl = levels[li];
        const wg = worlds.get(lvl.id);
        if (!wg) continue;
        const existing = new Map<string, THREE.Object3D>();
        wg.children.forEach((c) => { if (c.userData?.id) existing.set(c.userData.id, c); });

        const wantedIds = new Set(lvl.objects.map((o) => o.id));
        for (const [id, child] of existing) {
          if (!wantedIds.has(id)) {
            wg.remove(child);
            if (child.userData?.generatedText) disposeGeneratedObject(child);
            else if (child.userData?.generatedRow) disposeRackRowObject(child);
            (child as THREE.Mesh).geometry?.dispose?.();
          }
        }
        for (const o of lvl.objects) {
          let inst = existing.get(o.id);
          if (!inst) {
            inst = isTextLabelKind(o.kind)
              ? createTextLabelObject(o, DEFAULT_ACCENT_HEX)
              : isRackRowKind(o.kind)
                ? await createRackRowObject(o)
                : await getInstanceOrPlaceholder(o.kind);
            if (cancelled) return;
            inst.userData.id = o.id;
            inst.userData.kind = o.kind;
            if (isTextLabelKind(o.kind)) inst.userData.textObjectTag = textObjectTag(o, DEFAULT_ACCENT_HEX);
            if (isRackRowKind(o.kind)) inst.userData.rackRowTag = rackRowTag(o);
            inst.traverse((c) => {
              if ((c as THREE.Mesh).isMesh) {
                c.castShadow = true; c.receiveShadow = true;
              }
            });
            // Register emissive sub-meshes for the per-object blink animation
            // (clones mat-emissive per-instance so each rack pulses on its own
            // phase rather than in lockstep).
            registerBlinkInstance(inst, o.id);
            wg.add(inst);
            addedAny = true;
          } else if (isTextLabelKind(o.kind)) {
            const nextTextTag = textObjectTag(o, DEFAULT_ACCENT_HEX);
            if (inst.userData.textObjectTag !== nextTextTag) {
              const next = createTextLabelObject(o, DEFAULT_ACCENT_HEX);
              next.userData.id = o.id;
              next.userData.kind = o.kind;
              next.userData.textObjectTag = nextTextTag;
              wg.remove(inst);
              if (inst.userData.generatedText) disposeGeneratedObject(inst);
              wg.add(next);
              inst = next;
              existing.set(o.id, next);
            }
          } else if (isRackRowKind(o.kind)) {
            const nextRowTag = rackRowTag(o);
            if (inst.userData.rackRowTag !== nextRowTag) {
              const next = await createRackRowObject(o);
              if (cancelled) return;
              next.userData.id = o.id;
              next.userData.kind = o.kind;
              next.userData.rackRowTag = nextRowTag;
              wg.remove(inst);
              if (inst.userData.generatedRow) disposeRackRowObject(inst);
              wg.add(next);
              inst = next;
              existing.set(o.id, next);
            }
          }
          inst.userData.levelId = lvl.id;
          if (isBrandCanvasKind(o.kind)) {
            applyBrandCanvasTransform(inst, o, 0);
            const materialMode = resolveBrandCanvasMaterialMode(o);
            const appearanceTag = materialMode === "accent" ? `accent:${DEFAULT_ACCENT_HEX.toString(16)}` : materialMode;
            if (inst.userData.brandCanvasAppearanceTag !== appearanceTag) {
              inst.userData.brandCanvasAppearanceTag = appearanceTag;
              applyBrandCanvasAppearance(inst, materialMode, DEFAULT_ACCENT_HEX);
            }
          } else {
            inst.position.set(o.x, 0, o.z);
            inst.rotation.set(0, o.rotation, 0);
            inst.scale.setScalar(o.scale);
            if (o.accent) applyAccentOverride(inst);
            else clearAccentOverride(inst);
          }
          // Brand-mark decal: tag-keyed so we don't re-load on every drag.
          // Placement options are included in the tag so controls trigger a
          // re-apply (cheap: same GLB clone path, just different transform).
          const oThickness = o.brandThickness ?? 0.15;
          const brandMarkMaterialMode = resolveBrandMarkMaterialMode(o);
          const tag = [
            o.brandMark ?? "none",
            o.brandSide ?? "none",
            oThickness.toFixed(2),
            o.brandFlip ? "flip" : "normal",
            o.brandInvert ? "inv" : "noinv",
            brandMarkMaterialMode === "accent" ? `accent:${DEFAULT_ACCENT_HEX.toString(16)}` : brandMarkMaterialMode,
          ].join(":");
          if (inst.userData.brandMarkTag !== tag) {
            inst.userData.brandMarkTag = tag;
            if (o.brandMark && o.brandSide) {
              void applyBrandMark(inst, o.brandMark, o.brandSide, oThickness, !!o.brandFlip, !!o.brandInvert, brandMarkMaterialMode, DEFAULT_ACCENT_HEX);
            } else {
              clearBrandMark(inst);
            }
          }
        }
      }
      // Kick the link rebuild after async GLB mounts so it reads real
      // bboxes from the new instances on first load.
      if (addedAny && !cancelled) {
        setInstancesVersion((v) => v + 1);
      }
    })();
    return () => { cancelled = true; };
  }, [levels, manifest]);

  // Rebuild per-level links groups whenever the relevant inputs change.
  useEffect(() => {
    const scene = sceneRef.current;
    const worlds = worldsRef.current;
    const linksGroups = linksGroupsRef.current;
    if (!scene || !worlds || !linksGroups) return;

    // Drop link groups for levels that no longer exist.
    const liveIds = new Set(levels.map((l) => l.id));
    for (const [id, lg] of linksGroups) {
      if (!liveIds.has(id)) {
        scene.remove(lg);
        linksGroups.delete(id);
      }
    }

    // Match Scene view's resolveLinkColor: monochrome white-ish on dark
    // unless the profile explicitly sets `linkColor`. Iso is dark-only so
    // the dark default is the only branch needed.
    const linkColor = new THREE.Color(settings.linkColor || "#dadbe0");
    for (const lvl of levels) {
      let lg = linksGroups.get(lvl.id);
      if (!lg) {
        lg = new THREE.Group();
        lg.name = `iso-links-${lvl.id}`;
        scene.add(lg);
        linksGroups.set(lvl.id, lg);
      }
      const wg = worlds.get(lvl.id);
      if (!wg) continue;
      rebuildLinks(lg, lvl.links ?? [], {
        objectsById: new Map(lvl.objects.map((o) => [o.id, {
          id: o.id, kind: o.kind, x: o.x, z: o.z, scale: o.scale, rotation: o.rotation,
        } as any])),
        worldGroup: wg,
        thickness: settings.linkThickness ?? 0.025,
        color: linkColor,
        straight: !!settings.straightLinks,
        cableOpacity: settings.linkCableOpacity ?? 0.55,
        stripOpacity: settings.linkStripOpacity ?? 0.6,
        power: settings.linkPower ?? true,
      });
    }
  }, [
    levels,
    settings.linkThickness,
    settings.linkCableOpacity,
    settings.linkStripOpacity,
    settings.linkPower,
    settings.linkColor,
    settings.straightLinks,
    // Re-fire after the world-sync effect actually mounts new instances,
    // so the link rebuild reads non-empty bboxes on first load.
    instancesVersion,
  ]);

  // The plate BODY (MeshStandardMaterial) renders at full opacity regardless
  // of which level is active. Dot-grid overlay planes use MeshBasicMaterial
  // with their own opacity from settings.dotGridOpacity — leave them alone,
  // otherwise this effect clobbers their transparency on first load and the
  // floors look black until the user nudges a dot-grid slider (which triggers
  // a full rebuildPlatesAndWorlds).
  useEffect(() => {
    const plates = platesRef.current;
    if (!plates) return;
    plates.children.forEach((c) => {
      const mesh = c as THREE.Mesh;
      const m = mesh.material as THREE.Material | undefined;
      if (!m) return;
      // Only touch the plate body. Dot grid + bottom-seam materials own their
      // own opacity contract.
      if ((m as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
        const sm = m as THREE.MeshStandardMaterial;
        sm.opacity = 1.0;
        sm.transparent = false;
        sm.needsUpdate = true;
      }
    });
  }, [activeLevelId]);

  // Selection outline. Driven from the render loop (see tick() below) so it
  // appears the frame the GLB instance mounts (avoids the "auto-add then
  // selectedId set, but instance isn't in world yet" race) and tracks drags.
  // The helper itself is materialised lazily — we only allocate when there's
  // a current selection.

  // ---------- actions ----------
  const addToActive = useCallback((kind: SceneKind) => {
    const newId = uid();
    pushSnapshot();
    setLevels((prev) =>
      prev.map((l) => {
        if (l.id !== activeLevelId) return l;
        // Drop the new object next to the currently selected one so the
        // user doesn't have to drag it into place every time. Step +X in
        // 2 m increments from the selection until a clear slot opens up.
        const selId = selectedIdRef.current;
        const sel = selId ? l.objects.find((p) => p.id === selId) : undefined;
        let nx = 0;
        let nz = 0;
        if (sel) {
          const STEP = 2;
          const PROXIMITY = 1.2;
          nx = sel.x + STEP;
          nz = sel.z;
          const clash = (x: number, z: number) =>
            l.objects.some((p) => p.id !== sel.id && Math.hypot(p.x - x, p.z - z) < PROXIMITY);
          let guard = 32;
          while (clash(nx, nz) && guard-- > 0) nx += STEP;
        }
        return {
          ...l,
          objects: [...l.objects, {
            id: newId, levelId: l.id, kind,
            x: nx, z: nz, scale: 1, rotation: 0,
            ...defaultBrandObjectPatch(kind),
            ...(isRackRowKind(kind) ? defaultRackRowPatch() : {}),
          }],
        };
      }),
    );
    // Select the new object so the floating SelectionCard appears immediately.
    setSelectedId(newId);
  }, [activeLevelId]);

  const exportPng = useCallback(() => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: true }, `oneqode-iso-${Date.now()}.png`);
  }, []);

  const exportPngTransparent = useCallback(() => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: false }, `oneqode-iso-${Date.now()}-transparent.png`);
  }, []);

  const copyPng = useCallback(async (includeBackground: boolean) => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    try {
      await copyToClipboard(r, s, c, { includeBackground });
      setToast({
        id: Date.now(),
        message: includeBackground ? "Copied PNG" : "Copied transparent PNG",
      });
    } catch (e) {
      alert((e as Error).message);
    }
  }, []);

  const resetCamera = useCallback(() => {
    setIsoPivotTarget();
    setSettings((p) => ({
      ...p,
      cameraYaw: DEFAULT_SETTINGS.cameraYaw,
      cameraPitch: DEFAULT_SETTINGS.cameraPitch,
      cameraDistance: DEFAULT_SETTINGS.cameraDistance,
    }));
  }, [setIsoPivotTarget]);

  // ---------- profile actions ----------
  const switchProfile = useCallback((ref: ProfileRef) => {
    void autoSaverRef.current.flush();
    setActiveRef(ref);
  }, []);

  const buildPayload = useCallback((name: string) => ({
    version: 1,
    kind: "iso-profile" as const,
    name,
    settings,
    levels,
    updatedAt: Date.now(),
  }), [settings, levels]);

  // Single host-routed "+ New profile" — server file on localhost, local
  // entry otherwise. The Save target is implicit (matches autosave).
  const newProfile = useCallback(() => {
    const name = window.prompt(
      editor
        ? "Name for new server profile (committed to repo):"
        : "Name for new local profile (saved in your browser):",
      "Untitled scene",
    );
    if (!name) return;
    // Empty payload: inherits the active settings (camera, theme, dot
    // grid, etc.) but starts with a single blank level so the user gets
    // a clean canvas. Iso requires at least one level for the renderer
    // to mount its plate, world group, links group, and guides group.
    const emptyPayload = {
      version: 1,
      kind: "iso-profile" as const,
      name,
      settings,
      levels: [{
        id: uid(),
        label: "Floor 1",
        objects: [],
        links: [],
        guides: [],
      }],
      updatedAt: Date.now(),
    };
    if (editor) {
      const slug = slugFromName("iso", name);
      if (slug === "iso-main") { alert("Reserved slug. Pick a different name."); return; }
      void (async () => {
        await fetch(`/__save-profile/${slug}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emptyPayload),
        });
        const all = await listServerProfiles();
        setServerList(all.filter((e) => e.kind === "iso"));
        setActiveRef(makeServerRef("iso", slug, name));
      })();
    } else {
      const slug = newLocalSlug("iso");
      saveLocalProfile("iso", slug, name, emptyPayload);
      setLocalList(listLocalProfiles("iso"));
      setActiveRef(makeLocalRef("iso", slug, name));
    }
  }, [editor, settings]);

  const removeProfile = useCallback(async (ref: ProfileRef) => {
    if (ref.source === "server") {
      if (ref.slug === "iso-main") return;
      if (!editor) return;
      if (!confirm(`Delete server profile "${ref.name}"?`)) return;
      await deleteServerProfile(ref.slug);
      const all = await listServerProfiles();
      setServerList(all.filter((e) => e.kind === "iso"));
    } else {
      if (!confirm(`Delete local profile "${ref.name}"?`)) return;
      deleteLocalProfile("iso", ref.slug);
      setLocalList(listLocalProfiles("iso"));
    }
    if (activeRef.source === ref.source && activeRef.slug === ref.slug) {
      setActiveRef(defaultMainRef("iso"));
    }
  }, [editor, activeRef]);

  const renameLocal = useCallback((slug: string) => {
    const cur = listLocalProfiles("iso").find((p) => p.slug === slug);
    if (!cur) return;
    const name = window.prompt("Rename local profile:", cur.name);
    if (!name) return;
    renameLocalProfile("iso", slug, name);
    setLocalList(listLocalProfiles("iso"));
    if (activeRef.source === "local" && activeRef.slug === slug) {
      setActiveRef({ ...activeRef, name });
    }
  }, [activeRef]);

  /** Save the current iso scene as a new local profile (copies levels +
   *  settings; different from `newProfile` which starts empty). */
  const duplicateToLocal = useCallback(() => {
    const defaultName = `${activeRef.name} (copy)`;
    const name = window.prompt(
      "Name for the local copy (saved in your browser):",
      defaultName,
    );
    if (!name) return;
    const slug = newLocalSlug("iso");
    const payload = {
      version: 1,
      kind: "iso-profile" as const,
      name,
      settings,
      levels: levelsRef.current,
      updatedAt: Date.now(),
    };
    saveLocalProfile("iso", slug, name, payload);
    setLocalList(listLocalProfiles("iso"));
    setActiveRef(makeLocalRef("iso", slug, name));
  }, [activeRef.name, settings]);

  const exportActiveAsJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(buildPayload(activeRef.name), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeRef.slug}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [activeRef, buildPayload]);

  const importJsonFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.settings || !Array.isArray(parsed?.levels)) throw new Error("Missing settings/levels");
      const slug = newLocalSlug("iso");
      const name = parsed.name ? `${parsed.name} (imported)` : "Imported iso";
      saveLocalProfile("iso", slug, name, { ...parsed, name });
      setLocalList(listLocalProfiles("iso"));
      setActiveRef(makeLocalRef("iso", slug, name));
    } catch (e) {
      alert(`Import failed: ${(e as Error).message}`);
    }
  }, []);

  const activeLevel = useMemo(
    () => levels.find((l) => l.id === activeLevelId) ?? levels[0],
    [levels, activeLevelId],
  );

  const selectedObject = useMemo<IsoObject | null>(() => {
    if (!selectedId) return null;
    for (const l of levels) {
      const o = l.objects.find((x) => x.id === selectedId);
      if (o) return o;
    }
    return null;
  }, [levels, selectedId]);

  const updateSelectedObject = useCallback((patch: Partial<IsoObject>) => {
    if (!selectedId) return;
    setLevels((prev) =>
      prev.map((l) => ({
        ...l,
        objects: l.objects.map((o) =>
          o.id === selectedId ? { ...o, ...patch } : o,
        ),
      })),
    );
  }, [selectedId]);

  const addLinkFromSelectedTo = useCallback((toId: string) => {
    if (!selectedId || !selectedObject) return;
    const lvlId = selectedObject.levelId;
    setLevels((prev) =>
      prev.map((l) => {
        if (l.id !== lvlId) return l;
        const links = l.links ?? [];
        // Don't create dupes.
        if (links.some((k) => (k.fromId === selectedId && k.toId === toId) || (k.fromId === toId && k.toId === selectedId))) {
          return l;
        }
        return { ...l, links: [...links, { id: uid(), fromId: selectedId, toId }] };
      }),
    );
  }, [selectedId, selectedObject]);

  const disconnectIsoLink = useCallback((linkId: string) => {
    if (!selectedObject) return;
    const lvlId = selectedObject.levelId;
    setLevels((prev) =>
      prev.map((l) => {
        if (l.id !== lvlId) return l;
        const links = (l.links ?? []).filter((k) => k.id !== linkId);
        return { ...l, links };
      }),
    );
  }, [selectedObject]);

  const removeLinksFromSelected = useCallback(() => {
    if (!selectedId || !selectedObject) return;
    const lvlId = selectedObject.levelId;
    setLevels((prev) =>
      prev.map((l) => {
        if (l.id !== lvlId) return l;
        const links = (l.links ?? []).filter((k) => k.fromId !== selectedId && k.toId !== selectedId);
        return { ...l, links };
      }),
    );
  }, [selectedId, selectedObject]);

  const removeSelected = useCallback(() => {
    if (!selectedId || !selectedObject) return;
    const lvlId = selectedObject.levelId;
    setLevels((prev) =>
      prev.map((l) => {
        if (l.id !== lvlId) return l;
        const links = (l.links ?? []).filter((k) => k.fromId !== selectedId && k.toId !== selectedId);
        return {
          ...l,
          objects: l.objects.filter((o) => o.id !== selectedId),
          links,
        };
      }),
    );
    setSelectedId(null);
  }, [selectedId, selectedObject]);

  // Group the catalog by SceneKindGroup for the left-panel "Add object" UI.
  const catalogGroups = useMemo(() => {
    const m = new Map<string, CatalogEntry[]>();
    const order = ["AI", "Infra", "Network", "Power", "Site", "Brand"];
    for (const g of order) m.set(g, []);
    if (manifest) {
      for (const e of manifest.catalog) {
        if (!m.has(e.group)) m.set(e.group, []);
        m.get(e.group)!.push(e);
      }
    }
    return addVirtualRackRowEntry(addVirtualBrandEntries(m));
  }, [manifest]);

  if (error) return <div style={styles.errScreen}>{error}</div>;
  if (!manifest) return <div style={styles.errScreen}>Loading manifest…</div>;

  return (
    <div style={styles.root}>
      <div ref={mountRef} style={styles.canvasMount} />

      <div style={styles.topBar}>
        <div style={styles.branding}>
          <span style={{ fontWeight: 600 }}>OneQode</span>
          <span style={{ color: "var(--muted)" }}>Isometric builder</span>
        </div>
        {!editor && activeRef.source === "server" && (
          <span style={styles.readOnlyChip} title="Server profiles are read-only outside localhost — switch to a local profile to edit">
            Read-only
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button style={styles.btn} onClick={exportPng}>Export PNG</button>
        <button style={styles.btn} onClick={exportPngTransparent}>Export Transparent PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(true)}>Copy PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(false)}>Copy Transparent PNG</button>
        <button style={styles.btn} onClick={resetCamera}>Reset camera</button>
      </div>
      <Toast toast={toast} />

      {/* Left panel — profiles + add-object catalog (mirrors Scene builder) */}
      <aside style={styles.leftPanel}>
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Profiles</div>
          <ProfilePickerIso
            activeRef={activeRef}
            serverList={serverList}
            localList={localList}
            editor={editor}
            saveStatus={saveStatus}
            onSwitch={switchProfile}
            onNewProfile={newProfile}
            onDuplicateToLocal={duplicateToLocal}
            onDelete={removeProfile}
            onRenameLocal={renameLocal}
            onExport={exportActiveAsJson}
            onImportClick={() => fileInputRef.current?.click()}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importJsonFile(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Add to active level</div>
          {Array.from(catalogGroups.entries())
            .filter(([, entries]) => entries.length > 0)
            .map(([group, entries]) => (
              <div key={group} style={{ marginBottom: 8 }}>
                <div style={styles.groupHeader}>{group}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                  {entries.map((e) => {
                    // Local profiles live in localStorage and stay editable
                    // even off-localhost. Only server profiles need the
                    // localhost gate (they save back to the repo).
                    const canEdit = activeRef.source === "local" || editor;
                    const enabled = e.available && canEdit;
                    return (
                      <button
                        key={e.kind}
                        style={{
                          ...styles.catalogItem,
                          opacity: enabled ? 1 : 0.35,
                          cursor: enabled ? "pointer" : "not-allowed",
                        }}
                        disabled={!enabled}
                        onClick={() => addToActive(e.kind as SceneKind)}
                        title={
                          !e.available
                            ? `${e.label} (asset missing)`
                            : !canEdit
                              ? "Server profile is read-only outside localhost — switch to a local profile to edit"
                              : `Add ${e.label}`
                        }
                      >
                        {e.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </aside>

      {/* Floating selection card — anchored to the selected object in 3D */}
      {selectedObject && (() => {
        const lvl = levels.find((l) => l.id === selectedObject.levelId);
        const peers = lvl?.objects ?? [];
        const peerLabel = (id: string) => peers.find((o) => o.id === id)?.kind ?? "?";
        const connections = (lvl?.links ?? [])
          .filter((k) => k.fromId === selectedObject.id || k.toId === selectedObject.id)
          .map((k) => ({
            id: k.id,
            peerLabel: peerLabel(k.fromId === selectedObject.id ? k.toId : k.fromId),
          }));
        return (
          <SelectionCard
            ref={selectionCardRef}
            kind={selectedObject.kind}
            rotation={selectedObject.rotation}
            scale={selectedObject.scale}
            accent={!!selectedObject.accent}
            brandColor={isBrandCanvasKind(selectedObject.kind)
              ? resolveBrandCanvasMaterialMode(selectedObject)
              : resolveBrandMarkMaterialMode(selectedObject)}
            brandMark={selectedObject.brandMark ?? null}
            brandSide={selectedObject.brandSide ?? null}
            brandThickness={selectedObject.brandThickness ?? (isBrandCanvasKind(selectedObject.kind) ? 1 : 0.15)}
            brandFlip={!!selectedObject.brandFlip}
            brandInvert={!!selectedObject.brandInvert}
            isBrandCanvasObject={isBrandCanvasKind(selectedObject.kind)}
            isTextObject={isTextLabelKind(selectedObject.kind)}
            brandPose={selectedObject.brandPose ?? "upright"}
            textContent={selectedObject.textContent ?? "OneQode"}
            textFont={selectedObject.textFont ?? "sans"}
            textDepth={selectedObject.textDepth ?? 0.06}
            isRackRowObject={isRackRowKind(selectedObject.kind)}
            rowPreset={selectedObject.rowPreset ?? "medium"}
            rowCount={selectedObject.rowCount ?? 8}
            rowPair={!!selectedObject.rowPair}
            rowAisleWidth={selectedObject.rowAisleWidth ?? 2.4}
            linkingMode={linkingFromId === selectedObject.id}
            connections={connections}
            onChange={(patch) => updateSelectedObject(patch)}
            onStartLink={() => setLinkingFromId(selectedObject.id)}
            onCancelLink={() => setLinkingFromId(null)}
            onDisconnect={disconnectIsoLink}
            onClearLinks={removeLinksFromSelected}
            onDelete={removeSelected}
          />
        );
      })()}

      {/* Right panel — scene controls (mirrors Scene builder) */}
      <aside style={styles.rightPanel}>
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Lighting</div>
          <div style={styles.row}>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v1" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v1")}
              title="Original tuning"
            >
              V1
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v2" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v2")}
              title="Deeper blacks, stronger silhouette rim — matches the matte hero render"
            >
              V2
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v3" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v3")}
              title="Showroom-bright — chassis read as deep charcoal, highlights pop. Good for hero shots."
            >
              V3
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v4" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v4")}
              title="Hero / product-shot — near-black bg, warm key, cool fill+rim."
            >
              V4
            </button>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Levels (top → bottom)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {levels.map((l) => (
              <button
                key={l.id}
                onClick={() => { setActiveLevelId(l.id); setSelectedId(null); }}
                style={{
                  ...styles.levelRow,
                  outline: l.id === activeLevelId ? "1px solid var(--accent)" : "1px solid transparent",
                }}
              >
                {l.label}
                <span style={{ color: "var(--muted)", fontSize: 11 }}>{l.objects.length}</span>
              </button>
            ))}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 6 }}>
            Active: {activeLevel.label} · {activeLevel.objects.length} objects
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Camera</div>
          <div style={{ ...styles.kvRow, fontSize: 10, lineHeight: 1.5 }}>
            <span style={{ color: "var(--muted)" }}>Nav</span>
            <span style={{ color: "var(--muted)", textAlign: "right" }}>
              RMB orbit · scroll zoom
            </span>
          </div>

          {/* Iso quadrant presets — pick a starting angle. Each click also
              recentres the orbit pivot on the middle of the plate stack so
              the user gets a clean canonical view (clearing any prior pan). */}
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Quadrant</div>
          <div style={styles.row}>
            <button style={styles.btn} title="North-west"
              onClick={() => { setSettings((p) => ({ ...p, cameraYaw:  3 * Math.PI / 4 })); setIsoPivotTarget(); }}>NW</button>
            <button style={styles.btn} title="North-east"
              onClick={() => { setSettings((p) => ({ ...p, cameraYaw:  Math.PI / 4 })); setIsoPivotTarget(); }}>NE</button>
          </div>
          <div style={styles.row}>
            <button style={styles.btn} title="South-west"
              onClick={() => { setSettings((p) => ({ ...p, cameraYaw: -3 * Math.PI / 4 })); setIsoPivotTarget(); }}>SW</button>
            <button style={styles.btn} title="South-east"
              onClick={() => { setSettings((p) => ({ ...p, cameraYaw: -Math.PI / 4 })); setIsoPivotTarget(); }}>SE</button>
          </div>

          {/* Rotate the current view 90° / 180°. Yaw wraps to [-π, π] so
              repeated clicks don't drift the value out of range. */}
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>Rotate camera</div>
          <div style={styles.row}>
            <button style={styles.btn} title="Rotate 90° counter-clockwise"
              onClick={() => setSettings((p) => {
                let y = p.cameraYaw - Math.PI / 2;
                y = Math.atan2(Math.sin(y), Math.cos(y));
                return { ...p, cameraYaw: y };
              })}>↺ 90°</button>
            <button style={styles.btn} title="Rotate 90° clockwise"
              onClick={() => setSettings((p) => {
                let y = p.cameraYaw + Math.PI / 2;
                y = Math.atan2(Math.sin(y), Math.cos(y));
                return { ...p, cameraYaw: y };
              })}>↻ 90°</button>
            <button style={styles.btn} title="Flip 180°"
              onClick={() => setSettings((p) => {
                let y = p.cameraYaw + Math.PI;
                y = Math.atan2(Math.sin(y), Math.cos(y));
                return { ...p, cameraYaw: y };
              })}>⤺ 180°</button>
          </div>

          <SliderRow
            label="FOV"
            min={20} max={70} step={0.5}
            value={settings.fov}
            onChange={(v) => setSettings((p) => ({ ...p, fov: v }))}
          />
          <SliderRow
            label="Distance"
            min={2} max={200} step={0.5}
            value={settings.cameraDistance}
            onChange={(v) => setSettings((p) => ({ ...p, cameraDistance: v }))}
          />
          <SliderRow
            label="Height"
            min={CAMERA_PITCH_MIN} max={CAMERA_PITCH_MAX} step={0.01}
            value={settings.cameraPitch}
            onChange={(v) => setSettings((p) => ({ ...p, cameraPitch: v }))}
            fmt={formatCameraHeight}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Layout</div>
          <SliderRow
            label="Tile size"
            min={4} max={40} step={0.5}
            value={settings.tileSize}
            onChange={(v) => setSettings((p) => ({ ...p, tileSize: v }))}
          />
          <SliderRow
            label="Level spacing"
            min={3} max={12} step={0.25}
            value={settings.levelSpacing}
            onChange={(v) => setSettings((p) => ({ ...p, levelSpacing: v }))}
          />
          <SliderRow
            label="Plate thickness"
            min={0.05} max={1.5} step={0.05}
            value={settings.plateThickness ?? 0.40}
            onChange={(v) => setSettings((p) => ({ ...p, plateThickness: v }))}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Display</div>
          <Tabs<DisplayTab>
            value={displayTab}
            onChange={setDisplayTab}
            options={[
              { id: "dots", label: "Dot grid" },
              { id: "guides", label: "Guides" },
              { id: "links", label: "Links" },
            ]}
          />
          {displayTab === "dots" && (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <input
                  type="checkbox"
                  checked={settings.dotGrid !== false}
                  onChange={(e) => setSettings((p) => ({ ...p, dotGrid: e.target.checked }))}
                />
                Show dots
              </label>
              <SliderRow
                label="Spacing"
                min={0.25} max={3.0} step={0.05}
                value={settings.dotGridSpacing ?? 1.0}
                onChange={(v) => setSettings((p) => ({ ...p, dotGridSpacing: v }))}
              />
              <SliderRow
                label="Dot size"
                min={0.001} max={0.10} step={0.0005}
                value={settings.dotGridSize ?? 0.025}
                onChange={(v) => setSettings((p) => ({ ...p, dotGridSize: v }))}
              />
              <SliderRow
                label="Opacity"
                min={0} max={1} step={0.02}
                value={settings.dotGridOpacity ?? 0.55}
                onChange={(v) => setSettings((p) => ({ ...p, dotGridOpacity: v }))}
              />
            </>
          )}
          {displayTab === "guides" && (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <input
                  type="checkbox"
                  checked={!!settings.gridLines}
                  onChange={(e) => setSettings((p) => ({
                    ...defaultGuideSettings(),
                    ...p,
                    gridLines: e.target.checked,
                  }))}
                />
                Show guides
              </label>
              {settings.gridLines && (
                <>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>
                    Adds to active level: <strong>{levels.find((l) => l.id === activeLevelId)?.label ?? "—"}</strong>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      style={styles.btn}
                      onClick={() => {
                        const id = Math.random().toString(36).slice(2, 10);
                        const lvlId = activeLevelIdRef.current;
                        pushSnapshot();
                        setLevels((prev) => prev.map((l) =>
                          l.id !== lvlId ? l : { ...l, guides: [...(l.guides ?? []), { id, axis: "x", coord: 0 }] },
                        ));
                        setSelectedGuide({ levelId: lvlId, id });
                      }}
                    >+ Horizontal</button>
                    <button
                      style={styles.btn}
                      onClick={() => {
                        const id = Math.random().toString(36).slice(2, 10);
                        const lvlId = activeLevelIdRef.current;
                        pushSnapshot();
                        setLevels((prev) => prev.map((l) =>
                          l.id !== lvlId ? l : { ...l, guides: [...(l.guides ?? []), { id, axis: "z", coord: 0 }] },
                        ));
                        setSelectedGuide({ levelId: lvlId, id });
                      }}
                    >+ Vertical</button>
                  </div>
                  <SliderRow
                    label="Line opacity"
                    min={0} max={1} step={0.02}
                    value={settings.gridLinesOpacityDark ?? 0.55}
                    onChange={(v) => setSettings((p) => ({ ...p, gridLinesOpacityDark: v }))}
                  />
                  <SliderRow
                    label="Dot length"
                    min={0.01} max={0.6} step={0.01}
                    value={settings.gridLinesDashSize ?? 0.06}
                    onChange={(v) => setSettings((p) => ({ ...p, gridLinesDashSize: v }))}
                  />
                  <SliderRow
                    label="Dot gap"
                    min={0.02} max={1.5} step={0.02}
                    value={settings.gridLinesGapSize ?? 0.22}
                    onChange={(v) => setSettings((p) => ({ ...p, gridLinesGapSize: v }))}
                  />
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                    <input
                      type="checkbox"
                      checked={settings.gridLinesShowCrosses !== false}
                      onChange={(e) => setSettings((p) => ({ ...p, gridLinesShowCrosses: e.target.checked }))}
                    />
                    Crosses at intersections
                  </label>
                  {settings.gridLinesShowCrosses !== false && (
                    <SliderRow
                      label="Cross opacity"
                      min={0} max={1} step={0.02}
                      value={settings.gridLinesCrossOpacityDark ?? 0.75}
                      onChange={(v) => setSettings((p) => ({ ...p, gridLinesCrossOpacityDark: v }))}
                    />
                  )}
                  {selectedGuide && (
                    <button
                      style={styles.btn}
                      onClick={() => {
                        const sel = selectedGuide;
                        pushSnapshot();
                        setLevels((prev) => prev.map((l) =>
                          l.id !== sel.levelId ? l : {
                            ...l,
                            guides: (l.guides ?? []).filter((g) => g.id !== sel.id),
                          },
                        ));
                        setSelectedGuide(null);
                      }}
                    >Delete selected guide</button>
                  )}
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
                    Click a guide to select. Drag to move (Shift = no snap). Alt-drag duplicates. Backspace deletes.
                  </div>
                </>
              )}
            </>
          )}
          {displayTab === "links" && (
            <>
              <SliderRow
                label="Thickness"
                min={0.005} max={0.5} step={0.005}
                value={settings.linkThickness ?? 0.025}
                onChange={(v) => setSettings((p) => ({ ...p, linkThickness: v }))}
              />
              <SliderRow
                label="Cable opacity"
                min={0} max={1} step={0.02}
                value={settings.linkCableOpacity ?? 0.55}
                onChange={(v) => setSettings((p) => ({ ...p, linkCableOpacity: v }))}
              />
              <SliderRow
                label="Power opacity"
                min={0} max={1} step={0.02}
                value={settings.linkStripOpacity ?? 0.6}
                onChange={(v) => setSettings((p) => ({ ...p, linkStripOpacity: v }))}
              />
              <SliderRow
                label="Power speed"
                min={0} max={1} step={0.05}
                value={settings.linkSpeed ?? 0.2}
                onChange={(v) => setSettings((p) => ({ ...p, linkSpeed: v }))}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <input
                  type="checkbox"
                  checked={settings.linkPower ?? true}
                  onChange={(e) => setSettings((p) => ({ ...p, linkPower: e.target.checked }))}
                />
                Power on (animate flow)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <input
                  type="checkbox"
                  checked={!!settings.straightLinks}
                  onChange={(e) => setSettings((p) => ({ ...p, straightLinks: e.target.checked }))}
                />
                Straight links (no L-bend)
              </label>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function rebuildPlatesAndWorlds(
  plates: THREE.Group,
  worlds: Map<string, THREE.Group>,
  scene: THREE.Scene,
  levels: Level[],
  settings: IsoSettings,
) {
  // Tear down plates fully, but keep world groups by id so we don't reload GLBs.
  for (const child of [...plates.children]) {
    plates.remove(child);
    (child as THREE.Mesh).geometry?.dispose?.();
  }

  // Drop world groups for levels that no longer exist.
  const liveIds = new Set(levels.map((l) => l.id));
  for (const [id, wg] of worlds) {
    if (!liveIds.has(id)) {
      scene.remove(wg);
      worlds.delete(id);
    }
  }

  const total = levels.length;
  const tile = settings.tileSize;
  const thickness = settings.plateThickness ?? 0.40;
  // Resolve dot-grid params from settings with sensible defaults so legacy
  // saved profiles still render.
  const dotSpacing = settings.dotGridSpacing ?? 1.0;
  const dotSize = settings.dotGridSize ?? 0.025;
  const dotOpacity = settings.dotGridOpacity ?? 0.55;
  for (let i = 0; i < total; i++) {
    const lvl = levels[i];
    const y = (total - 1 - i) * settings.levelSpacing;

    // Plate body — matte plastic, lifted from `0x080a0d` so the floor reads
    // as a deliberate volume rather than a black void. envMap matches the
    // Scene tab's ground.
    const plateGeom = new THREE.BoxGeometry(tile, thickness, tile);
    const plateMat = applyOneQodeShader(new THREE.MeshStandardMaterial({
      color: 0x12151b,
      roughness: 0.92,
      metalness: 0,
      envMapIntensity: 0.10,
    }), "ground");
    const plate = new THREE.Mesh(plateGeom, plateMat);
    plate.position.set(0, y - thickness / 2, 0);
    plate.userData.levelId = lvl.id;
    plate.userData.floor = true;
    plate.receiveShadow = true;
    plates.add(plate);

    // Dot-grid overlay on the plate top — matches the scene-builder floor.
    const dotColorHex = 0xffffff;
    const dotTex = buildIsoPlateDotGrid(tile, dotSpacing, dotColorHex, dotSize);
    const dotGeom = new THREE.PlaneGeometry(tile, tile);
    const dotMat = new THREE.MeshBasicMaterial({
      map: dotTex,
      transparent: true,
      opacity: THREE.MathUtils.clamp(dotOpacity, 0, 1),
      depthWrite: false,
    });
    const dotPlane = new THREE.Mesh(dotGeom, dotMat);
    dotPlane.rotation.x = -Math.PI / 2;
    dotPlane.position.set(0, y + 0.001, 0);
    dotPlane.userData.levelId = lvl.id;
    dotPlane.userData.floor = true;
    dotPlane.userData.isDotGrid = true;
    dotPlane.visible = settings.dotGrid !== false; // default-on if unset
    plates.add(dotPlane);

    // Bottom-edge seam: a faint dark line where the plate underside meets
    // the side, defining the 3D volume of the floor without painting a
    // bright outline on top. Subtle by design — the user wanted the plate
    // to read as a slab, not as a CAD wireframe.
    const seamGeom = new THREE.EdgesGeometry(new THREE.PlaneGeometry(tile, tile));
    const bottomSeam = new THREE.LineSegments(
      seamGeom,
      new THREE.LineBasicMaterial({ color: 0x3a4048, transparent: true, opacity: 0.7 }),
    );
    bottomSeam.rotation.x = -Math.PI / 2;
    bottomSeam.position.set(0, y - thickness, 0);
    bottomSeam.userData.levelId = lvl.id;
    bottomSeam.userData.floor = true;
    plates.add(bottomSeam);

    // Ensure a world group exists for this level at the right Y.
    let wg = worlds.get(lvl.id);
    if (!wg) {
      wg = new THREE.Group();
      wg.name = `iso-world-${lvl.id}`;
      scene.add(wg);
      worlds.set(lvl.id, wg);
    }
    wg.userData.levelId = lvl.id;
    wg.position.set(0, y, 0);
  }
}

// ============================================================
// Tiny UI primitives
// ============================================================

function BrandMarkControlsIso(props: {
  brandMark: "icon" | "logo" | null;
  brandSide: "front" | "back" | "left" | "right" | "top" | null;
  onChange: (patch: { brandMark?: "icon" | "logo" | null; brandSide?: "front" | "back" | "left" | "right" | "top" | null }) => void;
}) {
  const { brandMark, brandSide, onChange } = props;
  const radio = (label: string, value: "icon" | "logo" | null) => (
    <label key={String(value)} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
      <input
        type="radio"
        name="iso-brand-mark"
        checked={brandMark === value}
        onChange={() => {
          if (value && !brandSide) onChange({ brandMark: value, brandSide: "top" });
          else if (!value) onChange({ brandMark: null, brandSide: null });
          else onChange({ brandMark: value });
        }}
      />
      {label}
    </label>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, marginTop: 4 }}>
      <div style={{ color: "var(--muted)" }}>Brand mark</div>
      <div style={{ display: "flex", gap: 8 }}>
        {radio("None", null)}
        {radio("Icon", "icon")}
        {radio("Logo", "logo")}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 2, opacity: brandMark ? 1 : 0.45 }}>
        Side
        <select
          value={brandSide ?? "top"}
          disabled={!brandMark}
          onChange={(e) => onChange({ brandSide: e.target.value as "front" | "back" | "left" | "right" | "top" })}
          style={{ flex: 1, fontSize: 11 }}
        >
          <option value="front">Front</option>
          <option value="back">Back</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="top">Top</option>
        </select>
      </label>
    </div>
  );
}

function SliderRow({
  label, min, max, step, value, onChange, fmt,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  fmt?: (v: number) => string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ display: "flex", justifyContent: "space-between", color: "var(--muted)", fontSize: 11 }}>
        {label}<span style={{ color: "var(--fg)" }}>{fmt ? fmt(value) : value.toFixed(2)}</span>
      </span>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

// Iso-local profile picker. Mirrors the Scene one; we keep them as separate
// components to avoid prop-shape drift, since the two builders evolve
// independently (level-aware vs flat).
function ProfilePickerIso(props: {
  activeRef: ProfileRef;
  serverList: Array<{ slug: string; kind: "scene" | "iso" }>;
  localList: Array<{ slug: string; name: string }>;
  editor: boolean;
  saveStatus: "idle" | "saving" | "saved" | "error";
  onSwitch: (ref: ProfileRef) => void;
  onNewProfile: () => void;
  /** Save the current iso scene (levels + settings) as a new local
   *  profile. Different from onNewProfile, which starts blank. */
  onDuplicateToLocal?: () => void;
  onDelete: (ref: ProfileRef) => void;
  onRenameLocal: (slug: string) => void;
  onExport: () => void;
  onImportClick: () => void;
}) {
  const {
    activeRef, serverList, localList, editor, saveStatus,
    onSwitch, onNewProfile, onDuplicateToLocal, onDelete, onRenameLocal,
    onExport, onImportClick,
  } = props;

  function nameForServer(slug: string): string {
    if (slug === "iso-main") return "Main (Reiss)";
    return slug.replace(/^iso-/, "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const status =
    saveStatus === "saving" ? "Saving…"
    : saveStatus === "saved" ? "Saved"
    : saveStatus === "error" ? "Save failed"
    : (activeRef.source === "server" && !editor) ? "Read-only"
    : activeRef.source === "server" ? "Editor mode (localhost)"
    : "Local profile";

  const list = (
    <>
      {serverList.length > 0 && (
        <>
          <div style={styles.groupHeader}>Server</div>
          {serverList.map((e) => {
            const name = nameForServer(e.slug);
            const ref = makeServerRef("iso", e.slug, name);
            const active = activeRef.source === "server" && activeRef.slug === e.slug;
            const canDelete = editor && e.slug !== "iso-main";
            return (
              <div
                key={e.slug}
                style={{ ...styles.profileRow, outline: active ? "1px solid var(--accent)" : "1px solid transparent" }}
              >
                <button style={styles.profileName} onClick={() => onSwitch(ref)} title={e.slug}>{name}</button>
                {canDelete && <button style={styles.miniBtn} onClick={() => onDelete(ref)}>×</button>}
              </div>
            );
          })}
        </>
      )}
      {localList.length > 0 && (
        <>
          <div style={{ ...styles.groupHeader, marginTop: 8 }}>Local (this browser)</div>
          {localList.map((e) => {
            const ref = makeLocalRef("iso", e.slug, e.name);
            const active = activeRef.source === "local" && activeRef.slug === e.slug;
            return (
              <div
                key={e.slug}
                style={{ ...styles.profileRow, outline: active ? "1px solid var(--accent)" : "1px solid transparent" }}
              >
                <button
                  style={styles.profileName}
                  onClick={() => onSwitch(ref)}
                  onDoubleClick={() => onRenameLocal(e.slug)}
                  title="Click: switch · Dbl-click: rename"
                >
                  {e.name}
                </button>
                <button style={styles.miniBtn} onClick={() => onDelete(ref)}>×</button>
              </div>
            );
          })}
        </>
      )}
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>{status}</div>
      {!editor && activeRef.source === "server" && (
        <div style={{ fontSize: 10, color: "var(--muted)", lineHeight: 1.4, marginBottom: 6 }}>
          You can drag and edit this scene, but changes won't save. Use{" "}
          <strong style={{ color: "var(--fg)" }}>+ Duplicate to local</strong>{" "}
          to keep your edits in this browser.
        </div>
      )}
      {list}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
        <button
          style={styles.btn}
          onClick={onNewProfile}
          title={editor
            ? "Create a new (empty) server profile"
            : "Create a new (empty) local profile"}
        >
          + New
        </button>
        {onDuplicateToLocal && (
          <button
            style={styles.btn}
            onClick={onDuplicateToLocal}
            title="Save a copy of the current scene as a new local profile"
          >
            + Duplicate
          </button>
        )}
        <button style={styles.btn} onClick={onExport}>Export</button>
        <button style={styles.btn} onClick={onImportClick}>Import</button>
      </div>
    </div>
  );
}

const PANEL_BG = "rgba(15, 15, 18, 0.86)";
const PANEL_BORDER = "1px solid #1f1f24";

const styles: Record<string, React.CSSProperties> = {
  root: { position: "absolute", inset: 0, overflow: "hidden" },
  canvasMount: { position: "absolute", inset: 0 },
  topBar: {
    position: "absolute", top: 12, left: 12, right: 12,
    display: "flex", alignItems: "center", gap: 8,
    pointerEvents: "none",
  },
  branding: { display: "flex", gap: 8, fontSize: 13, pointerEvents: "auto" },
  btn: {
    background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "6px 10px", borderRadius: 3, cursor: "pointer", fontSize: 11,
    pointerEvents: "auto",
  },
  leftPanel: {
    position: "absolute", top: 56, left: 12, bottom: 12, width: 240,
    overflowY: "auto", background: PANEL_BG, border: PANEL_BORDER, borderRadius: 6,
    padding: 12, fontSize: 12,
  },
  rightPanel: {
    position: "absolute", top: 56, right: 12, bottom: 12, width: 280,
    overflowY: "auto", background: PANEL_BG, border: PANEL_BORDER, borderRadius: 6,
    padding: 12, fontSize: 12,
  },
  section: { marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--line)" },
  sectionTitle: {
    fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5,
    color: "var(--muted)", marginBottom: 8,
  },
  levelRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "var(--bg)", color: "var(--fg)", border: "1px solid #1f1f24",
    padding: "8px 10px", borderRadius: 3, cursor: "pointer", fontSize: 12, textAlign: "left",
  },
  catalogItem: {
    background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "6px 4px", borderRadius: 3, fontSize: 11, cursor: "pointer",
    textAlign: "left",
  },
  groupHeader: {
    fontSize: 10, textTransform: "uppercase", letterSpacing: 1,
    color: "var(--muted)", marginBottom: 4,
  },
  profileRow: {
    display: "flex", alignItems: "center", gap: 4,
    padding: "4px 6px", borderRadius: 3, background: "var(--bg)", marginBottom: 2,
  },
  profileName: {
    flex: 1, background: "transparent", color: "var(--fg)", border: "none",
    textAlign: "left", cursor: "pointer", padding: 0, fontSize: 12,
  },
  miniBtn: {
    background: "transparent", color: "var(--muted)", border: "none",
    cursor: "pointer", fontSize: 14, padding: "2px 4px",
  },
  row: { display: "flex", gap: 4 },
  kvRow: { display: "flex", justifyContent: "space-between" },
  readOnlyChip: {
    background: "rgba(160, 160, 170, 0.12)",
    color: "var(--muted)",
    border: "1px solid #2a2a30",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
    pointerEvents: "auto",
    marginLeft: 8,
  },
  errScreen: {
    position: "absolute", inset: 0, display: "flex", alignItems: "center",
    justifyContent: "center", color: "var(--fg)", padding: 32,
  },
};
